import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { config } from '../../config/appConfig';

/* ══════════════════════════════════════════
   SOUND ENGINE
   ══════════════════════════════════════════ */
class SFX {
    constructor() { this.c = null; this.on = true; this.vol = 0.25; }
    init() {
        if (this.c) return;
        try { this.c = new (window.AudioContext || window.webkitAudioContext)(); } catch { this.on = false; }
    }
    _o(type, f0, f1, t0, dur, v) {
        if (!this.on || !this.c) return;
        const o = this.c.createOscillator(), g = this.c.createGain();
        o.connect(g); g.connect(this.c.destination);
        o.type = type;
        o.frequency.setValueAtTime(f0, t0);
        if (f1) o.frequency.exponentialRampToValueAtTime(Math.max(f1, 20), t0 + dur * 0.8);
        g.gain.setValueAtTime(v * this.vol, t0);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
        o.start(t0); o.stop(t0 + dur + 0.01);
    }
    _n(t0, dur, v) {
        if (!this.on || !this.c) return;
        const sr = this.c.sampleRate, len = sr * dur;
        const buf = this.c.createBuffer(1, len, sr), d = buf.getChannelData(0);
        for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2);
        const s = this.c.createBufferSource(); s.buffer = buf;
        const f = this.c.createBiquadFilter(); f.type = 'lowpass';
        f.frequency.setValueAtTime(2000, t0); f.frequency.exponentialRampToValueAtTime(200, t0 + dur);
        const g = this.c.createGain(); g.gain.setValueAtTime(v * this.vol, t0);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
        s.connect(f); f.connect(g); g.connect(this.c.destination); s.start(t0);
    }
    jump() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 280, 520, t, 0.11, 0.3); this._o('triangle', 420, 680, t + 0.015, 0.07, 0.08); }
    superJump() { const t = this.c?.currentTime; if (!t && t !== 0) return; for (let i = 0; i < 3; i++) this._o(i ? 'triangle' : 'sine', 300 + i * 200, (300 + i * 200) * 2.2, t + i * 0.035, 0.18, 0.2); }
    breakPlat() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._n(t, 0.1, 0.25); }
    collect() { const t = this.c?.currentTime; if (!t && t !== 0) return;[523, 659, 784].forEach((f, i) => this._o('sine', f, null, t + i * 0.045, 0.15, 0.18)); }
    powerup() { const t = this.c?.currentTime; if (!t && t !== 0) return;[440, 554, 659, 880].forEach((f, i) => this._o('sine', f, f * 1.01, t + i * 0.055, 0.22, 0.16)); }
    shield() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 600, 900, t, 0.12, 0.16); this._o('triangle', 800, 1200, t + 0.04, 0.16, 0.12); }
    shieldBreak() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sawtooth', 500, 200, t, 0.18, 0.18); this._n(t + 0.05, 0.1, 0.15); }
    jetpack() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sawtooth', 100, 180, t, 0.25, 0.1); this._o('sine', 600, 1100, t, 0.12, 0.12); }
    dash() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 300, 800, t, 0.09, 0.2); this._o('triangle', 500, 950, t + 0.02, 0.1, 0.12); }
    combo(lv) { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 400 + lv * 70, (400 + lv * 70) * 1.4, t, 0.09, 0.12 + lv * 0.015); }
    gameOver() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sawtooth', 400, 80, t, 0.45, 0.18); this._o('sine', 300, 60, t + 0.08, 0.35, 0.12); }
    milestone() { const t = this.c?.currentTime; if (!t && t !== 0) return;[523, 659, 784, 1047].forEach((f, i) => this._o('sine', f, null, t + i * 0.06, 0.25, 0.14)); }
    teleport() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 800, 200, t, 0.2, 0.15); this._o('sine', 200, 800, t + 0.1, 0.2, 0.15); }
    blackhole() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 60, 40, t, 0.5, 0.1); this._o('sawtooth', 80, 50, t, 0.5, 0.06); }
    convey() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('triangle', 200, 250, t, 0.06, 0.08); }
    rocketWarn() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('square', 800, 800, t, 0.05, 0.1); this._o('square', 800, 800, t + 0.1, 0.05, 0.1); }
    biomeChange() { const t = this.c?.currentTime; if (!t && t !== 0) return;[330, 440, 550, 660, 880].forEach((f, i) => this._o('sine', f, f * 1.02, t + i * 0.08, 0.35, 0.1)); }
    unlock() { const t = this.c?.currentTime; if (!t && t !== 0) return;[660, 880, 1100, 1320].forEach((f, i) => this._o('sine', f, null, t + i * 0.07, 0.3, 0.12)); }
    event() { const t = this.c?.currentTime; if (!t && t !== 0) return;[440, 660, 880, 1100, 1320].forEach((f, i) => this._o('sine', f, f * 1.05, t + i * 0.05, 0.2, 0.14)); this._o('triangle', 220, 440, t, 0.3, 0.08); }
    meteor() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._n(t, 0.3, 0.2); this._o('sine', 200, 60, t, 0.4, 0.12); }
    gravShift() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 150, 400, t, 0.3, 0.1); this._o('sine', 400, 150, t + 0.15, 0.3, 0.1); }
    goldRush() { const t = this.c?.currentTime; if (!t && t !== 0) return;[523, 659, 784, 1047, 1319].forEach((f, i) => this._o('sine', f, null, t + i * 0.04, 0.12, 0.12)); }
    timeSlow() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 600, 100, t, 0.5, 0.1); this._o('triangle', 300, 80, t + 0.1, 0.4, 0.06); }
    phantom() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 800, 300, t, 0.15, 0.08); this._o('sine', 600, 200, t + 0.08, 0.15, 0.08); this._o('sine', 400, 100, t + 0.16, 0.15, 0.08); }
    warp() { const t = this.c?.currentTime; if (!t && t !== 0) return; for (let i = 0; i < 6; i++) this._o('sine', 200 + i * 150, 1200 - i * 100, t + i * 0.03, 0.15, 0.1); }
    tiny() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 1200, 1800, t, 0.08, 0.15); this._o('sine', 1400, 2000, t + 0.04, 0.08, 0.12); }
    giant() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 80, 60, t, 0.2, 0.15); this._o('triangle', 120, 80, t + 0.05, 0.2, 0.1); }
    magnetPull() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 200, 350, t, 0.08, 0.1); }
    split() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 500, 300, t, 0.1, 0.12); this._o('sine', 500, 700, t + 0.02, 0.1, 0.12); }
    gravPlat() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 150, 500, t, 0.25, 0.12); this._o('triangle', 100, 300, t + 0.05, 0.2, 0.08); }
    nearMiss() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 600, 800, t, 0.06, 0.1); }
    revive() { const t = this.c?.currentTime; if (!t && t !== 0) return;[262, 330, 392, 523, 659, 784].forEach((f, i) => this._o('sine', f, f * 1.02, t + i * 0.07, 0.3, 0.14)); }
    encounterStart() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 330, 660, t, 0.15, 0.15); this._o('triangle', 440, 880, t + 0.08, 0.15, 0.12); this._o('sine', 550, 1100, t + 0.16, 0.15, 0.1); }
    encounterComplete() { const t = this.c?.currentTime; if (!t && t !== 0) return;[523, 659, 784, 1047, 1319, 1568].forEach((f, i) => this._o('sine', f, null, t + i * 0.05, 0.2, 0.15)); }
    artifact() { const t = this.c?.currentTime; if (!t && t !== 0) return;[880, 1100, 1320, 1760].forEach((f, i) => this._o('sine', f, f * 1.05, t + i * 0.08, 0.25, 0.13)); this._o('triangle', 440, 880, t + 0.32, 0.3, 0.08); }
    achievement() { const t = this.c?.currentTime; if (!t && t !== 0) return;[523, 659, 784, 1047].forEach((f, i) => this._o('sine', f, null, t + i * 0.06, 0.2, 0.15)); this._o('triangle', 262, 523, t + 0.24, 0.3, 0.1); }
    megaEvent() { const t = this.c?.currentTime; if (!t && t !== 0) return; for (let i = 0; i < 8; i++) this._o('sine', 200 + i * 100, (200 + i * 100) * 2, t + i * 0.04, 0.3, 0.12); this._n(t + 0.32, 0.2, 0.1); }
    bounceWall() { const t = this.c?.currentTime; if (!t && t !== 0) return; this._o('sine', 350, 500, t, 0.08, 0.15); }
    epoch() { const t = this.c?.currentTime; if (!t && t !== 0) return;[262, 330, 392, 523, 659, 784, 1047, 1319].forEach((f, i) => this._o('sine', f, f * 1.03, t + i * 0.06, 0.35, 0.12)); this._o('triangle', 131, 262, t, 0.5, 0.06); }
    comboEvent() { const t = this.c?.currentTime; if (!t && t !== 0) return;[440, 550, 660, 880, 1100].forEach((f, i) => this._o('sine', f, f * 1.08, t + i * 0.04, 0.18, 0.14)); }
}

/* ══════════════════════════════════════════
   CONSTANTS — height now /12 for realistic feel
   ══════════════════════════════════════════ */
const G = 0.4, JF = -10.5, SJF = -17, MS = 6, MA = 0.7, MF = 0.82;
const PW = 28, PH = 32, PLW = 68, PLH = 14, COIN_SZ = 14, PU_SZ = 20;
const HEIGHT_SCALE = 12; // was 4, now 3x slower meter gain

const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand = (a, b) => Math.random() * (b - a) + a;
const randInt = (a, b) => Math.floor(rand(a, b + 1));
const easeOutBack = t => { const c = 2.7; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2); };
const easeOutElastic = t => t === 0 || t === 1 ? t : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * (2 * Math.PI) / 3) + 1;
const easeInOutCubic = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const hashStr = (s) => { let h = 0; for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; } return Math.abs(h); };

/* ══════════════════════════════════════════
   PLATFORM & POWERUP TYPES
   ══════════════════════════════════════════ */
const PT = {
    NORMAL: 0, MOVING: 1, BREAKING: 2, SPRING: 3, GHOST: 4,
    TELEPORT: 6, CONVEYOR: 7, ICE: 8, CRUMBLE_CHAIN: 9,
    YOURSIZE: 10, ORBITING: 11,
    MAGNET_PLAT: 12, PHASE: 14, SPLIT: 15,
    GRAVITY_PLAT: 16, TRAMPOLINE_CHAIN: 17, TIMED: 18,
};

const PUT = { JETPACK: 0, SHIELD: 1, MAGNET: 2, TIMESLOW: 3, TINY: 4, GIANT: 5 };

/* ══════════════════════════════════════════
   BIOMES — stretched ×3, plus procedural after
   ══════════════════════════════════════════ */
const BIOMES = [
    { name: 'DEEP SPACE', startM: 0, endM: 500, bg1: '#07060c', bg2: '#0c0816', platColor: '#7c3aed', platGlow: 'rgba(124,58,237,0.35)', accent: '#a855f7', accentGlow: 'rgba(168,85,247,0.4)', starColor: '#c084fc', particleColor: '#c084fc', bgElements: 'stars' },
    { name: 'NEON CITY', startM: 500, endM: 1100, bg1: '#0a0612', bg2: '#12081e', platColor: '#ec4899', platGlow: 'rgba(236,72,153,0.35)', accent: '#f472b6', accentGlow: 'rgba(244,114,182,0.4)', starColor: '#f9a8d4', particleColor: '#f9a8d4', bgElements: 'neonSigns' },
    { name: 'INFERNO', startM: 1100, endM: 1800, bg1: '#120804', bg2: '#1a0a06', platColor: '#ea580c', platGlow: 'rgba(234,88,12,0.35)', accent: '#fb923c', accentGlow: 'rgba(251,146,60,0.4)', starColor: '#fdba74', particleColor: '#fbbf24', bgElements: 'embers' },
    { name: 'ICE REALM', startM: 1800, endM: 2600, bg1: '#060c12', bg2: '#0a1420', platColor: '#0ea5e9', platGlow: 'rgba(14,165,233,0.35)', accent: '#38bdf8', accentGlow: 'rgba(56,189,248,0.4)', starColor: '#bae6fd', particleColor: '#7dd3fc', bgElements: 'snowflakes' },
    { name: 'MATRIX', startM: 2600, endM: 3500, bg1: '#020c02', bg2: '#041a04', platColor: '#22c55e', platGlow: 'rgba(34,197,94,0.35)', accent: '#4ade80', accentGlow: 'rgba(74,222,128,0.4)', starColor: '#86efac', particleColor: '#4ade80', bgElements: 'digitalRain' },
    { name: 'THE VOID', startM: 3500, endM: 4800, bg1: '#020202', bg2: '#050505', platColor: '#e879f9', platGlow: 'rgba(232,121,249,0.35)', accent: '#f0abfc', accentGlow: 'rgba(240,171,252,0.4)', starColor: '#f5d0fe', particleColor: '#e879f9', bgElements: 'voidPulse' },
    { name: 'GOLDEN REALM', startM: 4800, endM: 6500, bg1: '#0c0a02', bg2: '#1a1504', platColor: '#eab308', platGlow: 'rgba(234,179,8,0.35)', accent: '#facc15', accentGlow: 'rgba(250,204,21,0.4)', starColor: '#fef08a', particleColor: '#fde047', bgElements: 'goldenDust' },
    { name: 'CRIMSON ABYSS', startM: 6500, endM: 8500, bg1: '#0c0204', bg2: '#1a0408', platColor: '#dc2626', platGlow: 'rgba(220,38,38,0.35)', accent: '#f87171', accentGlow: 'rgba(248,113,113,0.4)', starColor: '#fca5a5', particleColor: '#f87171', bgElements: 'bloodRain' },
    { name: 'ETHEREAL', startM: 8500, endM: 11000, bg1: '#04080c', bg2: '#081018', platColor: '#67e8f9', platGlow: 'rgba(103,232,249,0.35)', accent: '#a5f3fc', accentGlow: 'rgba(165,243,252,0.4)', starColor: '#cffafe', particleColor: '#a5f3fc', bgElements: 'aurora' },
    { name: 'NEBULA', startM: 11000, endM: 15000, bg1: '#0a0410', bg2: '#14081c', platColor: '#c084fc', platGlow: 'rgba(192,132,252,0.35)', accent: '#d8b4fe', accentGlow: 'rgba(216,180,254,0.4)', starColor: '#e9d5ff', particleColor: '#c084fc', bgElements: 'nebula' },
    { name: 'QUANTUM REALM', startM: 15000, endM: 22000, bg1: '#020808', bg2: '#041414', platColor: '#2dd4bf', platGlow: 'rgba(45,212,191,0.35)', accent: '#5eead4', accentGlow: 'rgba(94,234,212,0.4)', starColor: '#99f6e4', particleColor: '#5eead4', bgElements: 'quantum' },
    { name: 'CHAOS', startM: 22000, endM: 30000, bg1: '#060206', bg2: '#0a040a', platColor: '#f472b6', platGlow: 'rgba(244,114,182,0.35)', accent: '#fb7185', accentGlow: 'rgba(251,113,133,0.4)', starColor: '#fda4af', particleColor: '#fb7185', bgElements: 'chaosField' },
];

// Procedural biome generator for heights beyond hardcoded biomes
function getProceduralBiome(maxH) {
    const last = BIOMES[BIOMES.length - 1];
    if (maxH < last.endM) return null;

    // Cycle through hues smoothly
    const cycle = (maxH - last.endM) / 8000; // new biome feel every ~8000m
    const hue = (cycle * 137.508) % 360; // golden angle for max variety
    const sat = 60 + Math.sin(cycle * 2.1) * 15;
    const light = 55 + Math.sin(cycle * 1.7) * 10;

    const col = `hsl(${hue},${sat}%,${light}%)`;
    const colDark = `hsl(${hue},${sat}%,${light * 0.4}%)`;
    const colGlow = `hsla(${hue},${sat}%,${light}%,0.35)`;
    const bg1 = `hsl(${hue},${Math.max(10, sat - 40)}%,3%)`;
    const bg2 = `hsl(${hue},${Math.max(10, sat - 35)}%,6%)`;

    const names = ['ASTRAL PLANE', 'COSMIC DRIFT', 'STARWEAVE', 'LUMINOUS DEPTHS',
        'PRISMATIC VOID', 'ETERNAL DUSK', 'CRYSTAL EXPANSE', 'SHADOW REALM',
        'RADIANT CORE', 'TWILIGHT ZONE', 'DREAM LAYER', 'NOVA FIELD',
        'OBSIDIAN SKY', 'EMERALD TIDE', 'SAPPHIRE DEEP', 'AMBER HORIZON'];
    const nameIdx = Math.floor(cycle) % names.length;

    const bgEls = ['stars', 'nebula', 'aurora', 'quantum', 'voidPulse', 'embers', 'snowflakes'];
    const bgEl = bgEls[Math.floor(cycle * 1.3) % bgEls.length];

    return {
        name: names[nameIdx],
        startM: -1, endM: -1,
        bg1, bg2,
        platColor: col, platGlow: colGlow,
        accent: col, accentGlow: colGlow,
        starColor: `hsl(${hue},${sat * 0.6}%,${light + 20}%)`,
        particleColor: col,
        bgElements: bgEl,
        procedural: true,
    };
}

/* ══════════════════════════════════════════
   UNLOCKS — stretched ×3
   ══════════════════════════════════════════ */
const UNLOCKS = [
    { m: 150, type: 'plat', plat: PT.MOVING, name: 'Moving Platforms', icon: '◄►' },
    { m: 300, type: 'plat', plat: PT.BREAKING, name: 'Crumbling Platforms', icon: '💥' },
    { m: 450, type: 'pu', pu: PUT.SHIELD, name: 'Shield Powerup', icon: '🛡️' },
    { m: 600, type: 'plat', plat: PT.GHOST, name: 'Ghost Platforms', icon: '👻' },
    { m: 800, type: 'plat', plat: PT.SPRING, name: 'Spring Platforms', icon: '🔩' },
    { m: 1000, type: 'pu', pu: PUT.JETPACK, name: 'Jetpack', icon: '🚀' },
    { m: 1250, type: 'pu', pu: PUT.MAGNET, name: 'Coin Magnet', icon: '🧲' },
    { m: 1500, type: 'hazard', hazard: 'rocket', name: 'Rockets', icon: '🚀' },
    { m: 1800, type: 'plat', plat: PT.TELEPORT, name: 'Teleport Pads', icon: '🌀' },
    { m: 2100, type: 'hazard', hazard: 'blackhole', name: 'Black Holes', icon: '🕳️' },
    { m: 2400, type: 'plat', plat: PT.CONVEYOR, name: 'Conveyor Belts', icon: '⏩' },
    { m: 2800, type: 'plat', plat: PT.ICE, name: 'Ice Platforms', icon: '🧊' },
    { m: 3200, type: 'pu', pu: PUT.TIMESLOW, name: 'Time Slow', icon: '⏳' },
    { m: 3600, type: 'plat', plat: PT.CRUMBLE_CHAIN, name: 'Chain Crumble', icon: '⛓️' },
    { m: 4000, type: 'hazard', hazard: 'meteor', name: 'Meteor Showers', icon: '☄️' },
    { m: 4500, type: 'pu', pu: PUT.TINY, name: 'Shrink Pill', icon: '🔬' },
    { m: 5000, type: 'plat', plat: PT.ORBITING, name: 'Orbiting Platforms', icon: '🪐' },
    { m: 5500, type: 'pu', pu: PUT.GIANT, name: 'Giant Mode', icon: '🦖' },
    { m: 6500, type: 'plat', plat: PT.MAGNET_PLAT, name: 'Magnet Platforms', icon: '🧲' },
    { m: 7500, type: 'plat', plat: PT.YOURSIZE, name: 'Adaptive Platforms', icon: '📐' },
    { m: 8500, type: 'hazard', hazard: 'phantom', name: 'Phantom Stalker', icon: '👁️' },
    { m: 9500, type: 'plat', plat: PT.SPLIT, name: 'Split Platforms', icon: '✂️' },
    { m: 11000, type: 'hazard', hazard: 'homingMine', name: 'Homing Mines', icon: '💣' },
    { m: 13000, type: 'plat', plat: PT.PHASE, name: 'Phase Platforms', icon: '🔮' },
    { m: 15000, type: 'plat', plat: PT.TRAMPOLINE_CHAIN, name: 'Trampoline Chain', icon: '🎪' },
    { m: 17000, type: 'plat', plat: PT.GRAVITY_PLAT, name: 'Gravity Pads', icon: '🌀' },
    { m: 20000, type: 'plat', plat: PT.TIMED, name: 'Timer Platforms', icon: '⏱️' },
];

/* ══════════════════════════════════════════
   EVENTS — stretched & expanded
   ══════════════════════════════════════════ */
const EVT = {
    GOLD_RUSH: 'goldRush', LOW_GRAVITY: 'lowGravity', PLATFORM_RAIN: 'platformRain',
    COIN_FRENZY: 'coinFrenzy', SPEED_ZONE: 'speedZone', SAFE_HAVEN: 'safeHaven',
    BOUNCY_WORLD: 'bouncyWorld', GIFT_SHOWER: 'giftShower',
    SPIRAL_ASCENT: 'spiralAscent', GOLDEN_AGE: 'goldenAge',
    COIN_TRAIL: 'coinTrail', STARFALL: 'starfall', ZERO_G: 'zeroG',
    DOUBLE_JUMP: 'doubleJump', WIDE_PLATFORMS: 'widePlatforms',
    MAGNET_FIELD: 'magnetField', RAINBOW_ROAD: 'rainbowRoad',
};

const EVENT_DEFS = [
    { id: EVT.GOLD_RUSH, name: '💰 GOLD RUSH!', desc: 'Coins everywhere!', minM: 200, dur: 320, weight: 10, cooldown: 600 },
    { id: EVT.LOW_GRAVITY, name: '🪶 LOW GRAVITY', desc: 'Float higher!', minM: 300, dur: 380, weight: 8, cooldown: 700 },
    { id: EVT.SAFE_HAVEN, name: '🏝️ SAFE HAVEN', desc: 'A moment of peace...', minM: 400, dur: 280, weight: 7, cooldown: 800 },
    { id: EVT.COIN_FRENZY, name: '✨ COIN FRENZY', desc: 'Triple coin value!', minM: 600, dur: 340, weight: 8, cooldown: 650 },
    { id: EVT.SPEED_ZONE, name: '⚡ SPEED ZONE', desc: 'Everything faster!', minM: 800, dur: 300, weight: 6, cooldown: 750 },
    { id: EVT.BOUNCY_WORLD, name: '🔴 BOUNCY WORLD', desc: 'All platforms are springs!', minM: 1000, dur: 340, weight: 5, cooldown: 800 },
    { id: EVT.PLATFORM_RAIN, name: '🌧️ PLATFORM RAIN', desc: 'Platforms appear fast!', minM: 1200, dur: 320, weight: 5, cooldown: 850 },
    { id: EVT.SPIRAL_ASCENT, name: '🌀 SPIRAL ASCENT', desc: 'Round and round!', minM: 2000, dur: 380, weight: 4, cooldown: 950 },
    { id: EVT.GIFT_SHOWER, name: '🎁 GIFT SHOWER', desc: 'Powerups rain down!', minM: 2500, dur: 280, weight: 3, cooldown: 1100 },
    { id: EVT.GOLDEN_AGE, name: '👑 GOLDEN AGE', desc: 'Everything is x5 points!', minM: 3500, dur: 380, weight: 3, cooldown: 1300 },
    { id: EVT.COIN_TRAIL, name: '🌟 COIN TRAIL', desc: 'Follow the path!', minM: 4000, dur: 340, weight: 4, cooldown: 900 },
    { id: EVT.STARFALL, name: '⭐ STARFALL', desc: 'Stars rain down!', minM: 5500, dur: 320, weight: 3, cooldown: 950 },
    { id: EVT.ZERO_G, name: '🛸 ZERO GRAVITY', desc: 'Floating free!', minM: 7000, dur: 280, weight: 2, cooldown: 1100 },
    { id: EVT.WIDE_PLATFORMS, name: '🟩 MEGA PLATFORMS', desc: 'Everything is huge!', minM: 8500, dur: 340, weight: 3, cooldown: 900 },
    { id: EVT.MAGNET_FIELD, name: '🧲 MAGNET FIELD', desc: 'All coins attracted!', minM: 10000, dur: 340, weight: 3, cooldown: 1000 },
    { id: EVT.RAINBOW_ROAD, name: '🌈 RAINBOW ROAD', desc: 'A colorful path!', minM: 12000, dur: 380, weight: 2, cooldown: 1200 },
    { id: EVT.DOUBLE_JUMP, name: '🦘 DOUBLE JUMP', desc: 'Jump in mid-air!', minM: 14000, dur: 340, weight: 2, cooldown: 1100 },
];

/* ══════════════════════════════════════════
   COMBO EVENT SYSTEM — procedural infinite variety
   ══════════════════════════════════════════ */
const COMBO_EFFECTS = [
    { id: 'lowGrav', name: 'Low Gravity', apply: g => { g.gravMod = 0.5; } },
    { id: 'coinRain', name: 'Coin Rain', apply: (g, frame) => { if (frame % 18 === 0) g.coins.push(g.gen._mkCoin(rand(20, g.w - 20), g.camY + rand(50, g.h - 50))); } },
    { id: 'widePlats', name: 'Wide Platforms', apply: g => { g._comboWideMod = 1.6; } },
    { id: 'bouncyAll', name: 'Bouncy', apply: g => { g._comboPlatOverride = 'bouncy'; } },
    { id: 'speedUp', name: 'Speed', apply: g => { g.timeMod = 1.25; } },
    { id: 'safeMode', name: 'Safe', apply: g => { g._comboPlatOverride = 'safe'; g._comboGapMod = 0.7; } },
    { id: 'coinValue', name: 'Coin Boost', apply: g => { g._comboCoinMult = 4; } },
    {
        id: 'puRain', name: 'Powerup Rain', apply: (g, frame) => {
            if (frame % 90 === 0 && g.gen.unlockedPU.size > 0) {
                const avail = [...g.gen.unlockedPU];
                g.pus.push(g.gen._mkPU(rand(30, g.w - 50), g.camY + rand(40, g.h - 60), avail[randInt(0, avail.length - 1)]));
            }
        }
    },
    {
        id: 'magnetAll', name: 'Magnet', apply: g => {
            for (const c of g.coins) {
                if (c.collected) continue;
                const dx = (g.p.x + g.p.w / 2) - (c.x + c.w / 2);
                const dy = (g.p.y + g.p.h / 2) - (c.y + c.h / 2);
                const d = Math.hypot(dx, dy);
                if (d < 200 && d > 1) { c.x += dx / d * 2.5; c.y += dy / d * 2.5; }
            }
        }
    },
    { id: 'spiralGen', name: 'Spiral', apply: g => { g._comboSpiral = true; } },
];

const COMBO_ADJECTIVES = [
    'Radiant', 'Cosmic', 'Crystal', 'Shadow', 'Thunder', 'Prismatic',
    'Stellar', 'Mystic', 'Blazing', 'Frozen', 'Quantum', 'Ethereal',
    'Ancient', 'Luminous', 'Void', 'Solar', 'Lunar', 'Astral',
    'Crimson', 'Emerald', 'Obsidian', 'Sapphire', 'Amber', 'Jade',
];
const COMBO_NOUNS = [
    'Storm', 'Wave', 'Surge', 'Vortex', 'Cascade', 'Torrent',
    'Highway', 'Rush', 'Blast', 'Pulse', 'Stream', 'Drift',
    'Bloom', 'Tide', 'Glow', 'Burst', 'Flux', 'Nova',
    'Aurora', 'Zenith', 'Core', 'Nexus', 'Orbit', 'Eclipse',
];
const COMBO_ICONS = ['⚡', '🌈', '🔥', '❄️', '💎', '🌀', '✨', '💫', '🌟', '🪐', '🔮', '⭐', '🌊', '🎆', '💠', '🏵️'];

class ComboEventEngine {
    constructor() { this.reset(); }
    reset() {
        this.active = null; this.timer = 0; this.effects = [];
        this.sinceLastCombo = 0; this.generated = new Set();
        this.cooldown = 0;
    }
    update(maxH, game) {
        this.sinceLastCombo++;
        if (this.cooldown > 0) this.cooldown--;

        if (this.active) {
            this.timer--;
            // Apply effects each frame
            game._comboWideMod = 1;
            game._comboPlatOverride = null;
            game._comboGapMod = 1;
            game._comboCoinMult = 1;
            game._comboSpiral = false;
            for (const eff of this.effects) eff.apply(game, game.frame);
            if (this.timer <= 0) {
                this.active = null;
                this.effects = [];
                // Reset modifiers
                game._comboWideMod = 1;
                game._comboPlatOverride = null;
                game._comboGapMod = 1;
                game._comboCoinMult = 1;
                game._comboSpiral = false;
            }
            return null;
        }

        // Only start combo events after base events system is well used (high altitude)
        if (maxH < 5000) return null;
        if (this.cooldown > 0) return null;

        // Chance increases with height and time since last
        let chance = 0.002 + clamp(this.sinceLastCombo / 500, 0, 0.015) + clamp((maxH - 5000) / 100000, 0, 0.005);
        if (this.sinceLastCombo > 600) chance = 0.5;

        if (Math.random() < chance) {
            return this._generate(maxH);
        }
        return null;
    }

    _generate(maxH) {
        // Pick 2-3 random effects
        const count = maxH > 15000 ? 3 : 2;
        const shuffled = [...COMBO_EFFECTS].sort(() => Math.random() - 0.5);
        const picked = shuffled.slice(0, count);

        // Generate unique name
        const seed = picked.map(e => e.id).sort().join('+');
        const h = hashStr(seed + Math.floor(maxH / 1000));
        const adj = COMBO_ADJECTIVES[h % COMBO_ADJECTIVES.length];
        const noun = COMBO_NOUNS[(h >> 4) % COMBO_NOUNS.length];
        const icon = COMBO_ICONS[(h >> 8) % COMBO_ICONS.length];
        const name = `${icon} ${adj} ${noun}`;
        const desc = picked.map(e => e.name).join(' + ');

        this.active = { name, desc, seed };
        this.effects = picked;
        this.timer = 300 + randInt(0, 100);
        this.sinceLastCombo = 0;
        this.cooldown = 500 + randInt(0, 300);
        this.generated.add(seed);

        return this.active;
    }

    is() { return !!this.active; }
    progress() { return this.active ? 1 - this.timer / (300 + 50) : 0; }
}

/* ══════════════════════════════════════════
   ENCOUNTERS — stretched ×3
   ══════════════════════════════════════════ */
const ENCOUNTER_DEFS = [
    { id: 'staircase', name: '🏗️ Spiral Staircase', minM: 600, weight: 8, cooldown: 900 },
    { id: 'coinRing', name: '💫 Coin Galaxy', minM: 1000, weight: 7, cooldown: 700 },
    { id: 'zigzag', name: '⚡ Lightning Path', minM: 1500, weight: 6, cooldown: 800 },
    { id: 'pyramid', name: '🔺 The Pyramid', minM: 2500, weight: 5, cooldown: 1000 },
    { id: 'corridor', name: '🏚️ Narrow Pass', minM: 2000, weight: 6, cooldown: 850 },
    { id: 'springChain', name: '🔩 Spring Frenzy', minM: 1300, weight: 7, cooldown: 750 },
    { id: 'coinWaterfall', name: '💰 Coin Waterfall', minM: 1100, weight: 8, cooldown: 650 },
    { id: 'platformBridge', name: '🌉 Sky Bridge', minM: 2200, weight: 5, cooldown: 950 },
    { id: 'funnel', name: '🌪️ The Funnel', minM: 3500, weight: 4, cooldown: 1100 },
    { id: 'diamondPattern', name: '💎 Diamond Formation', minM: 4000, weight: 4, cooldown: 1000 },
    { id: 'helixRise', name: '🧬 Helix Rise', minM: 5000, weight: 3, cooldown: 1200 },
    { id: 'wavePath', name: '🌊 Wave Path', minM: 6000, weight: 3, cooldown: 1050 },
    { id: 'galaxySpiral', name: '🌀 Galaxy Spiral', minM: 8000, weight: 2, cooldown: 1400 },
    { id: 'theArchitect', name: '🏛️ The Architect', minM: 10000, weight: 2, cooldown: 1600 },
    { id: 'quantumLeap', name: '⚛️ Quantum Leap', minM: 15000, weight: 1, cooldown: 2200 },
];

/* ══════════════════════════════════════════
   MUTATORS — stretched ×3
   ══════════════════════════════════════════ */
const MUTATOR_DEFS = [
    { id: 'bounceWalls', name: '🏀 Bouncy Walls', desc: 'Wall bounces boost you up!', minM: 3000 },
    { id: 'speedDemon', name: '🏎️ Speed Demon', desc: 'Everything gets faster over time', minM: 6000 },
    { id: 'platDecay', name: '📉 Platform Decay', desc: 'Platforms slowly shrink', minM: 9000 },
    { id: 'echoJump', name: '🔊 Echo Jumps', desc: 'Every 3rd jump is a super jump!', minM: 12000 },
    { id: 'coinHunger', name: '🪙 Coin Hunger', desc: 'Collect coins for bonus score!', minM: 16000 },
    { id: 'magneticField', name: '🧲 Magnetic Pull', desc: 'Slight pull toward center', minM: 20000 },
];

/* ══════════════════════════════════════════
   ACHIEVEMENTS — stretched ×3
   ══════════════════════════════════════════ */
const ACHIEVEMENT_DEFS = [
    { id: 'first100', name: 'Sky Scraper', desc: 'Reach 100m', check: s => s.maxH >= 100, icon: '🏢' },
    { id: 'first500', name: 'Cloud Walker', desc: 'Reach 500m', check: s => s.maxH >= 500, icon: '☁️' },
    { id: 'first1000', name: 'Star Gazer', desc: 'Reach 1000m', check: s => s.maxH >= 1000, icon: '⭐' },
    { id: 'first5000', name: 'Galaxy Hopper', desc: 'Reach 5000m', check: s => s.maxH >= 5000, icon: '🌌' },
    { id: 'first10000', name: 'Cosmic Voyager', desc: 'Reach 10000m', check: s => s.maxH >= 10000, icon: '🚀' },
    { id: 'first25000', name: 'Dimension Walker', desc: 'Reach 25000m', check: s => s.maxH >= 25000, icon: '🌀' },
    { id: 'combo5', name: 'Combo King', desc: 'Get a x5 combo', check: s => s.maxCombo >= 5, icon: '👑' },
    { id: 'combo15', name: 'Combo God', desc: 'Get a x15 combo', check: s => s.maxCombo >= 15, icon: '⚡' },
    { id: 'combo30', name: 'Combo Legend', desc: 'Get a x30 combo', check: s => s.maxCombo >= 30, icon: '🔥' },
    { id: 'coins100', name: 'Coin Collector', desc: 'Collect 100 coins in one game', check: s => s.coinsCollected >= 100, icon: '🪙' },
    { id: 'coins500', name: 'Coin Hoarder', desc: 'Collect 500 coins in one game', check: s => s.coinsCollected >= 500, icon: '💰' },
    { id: 'noHit', name: 'Untouchable', desc: 'Reach 1500m without using shield', check: s => s.maxH >= 1500 && !s.usedShield, icon: '🛡️' },
    { id: 'allBiomes', name: 'Explorer', desc: 'Visit 8 different biomes', check: s => s.biomesVisited >= 8, icon: '🗺️' },
    { id: 'events5', name: 'Event Survivor', desc: 'Survive 5 events', check: s => s.eventsSurvived >= 5, icon: '🎪' },
    { id: 'encounter3', name: 'Encounter Master', desc: 'Complete 3 encounters', check: s => s.encountersCompleted >= 3, icon: '🏆' },
    { id: 'encounter10', name: 'Encounter Legend', desc: 'Complete 10 encounters', check: s => s.encountersCompleted >= 10, icon: '🌟' },
];

/* ══════════════════════════════════════════
   ARTIFACTS — stretched ×3
   ══════════════════════════════════════════ */
const ARTIFACT_DEFS = [
    { id: 'starFragment', name: 'Star Fragment', icon: '💫', minM: 300, maxM: 900, rarity: 0.08 },
    { id: 'moonstone', name: 'Moonstone', icon: '🌙', minM: 900, maxM: 1800, rarity: 0.06 },
    { id: 'solarCrystal', name: 'Solar Crystal', icon: '🔮', minM: 1800, maxM: 3000, rarity: 0.05 },
    { id: 'voidShard', name: 'Void Shard', icon: '🖤', minM: 3000, maxM: 4500, rarity: 0.04 },
    { id: 'ancientRelic', name: 'Ancient Relic', icon: '🏺', minM: 4500, maxM: 6500, rarity: 0.035 },
    { id: 'dragonScale', name: 'Dragon Scale', icon: '🐉', minM: 6500, maxM: 9000, rarity: 0.03 },
    { id: 'etherealGem', name: 'Ethereal Gem', icon: '💎', minM: 9000, maxM: 12000, rarity: 0.025 },
    { id: 'cosmicDust', name: 'Cosmic Dust', icon: '✨', minM: 12000, maxM: 17000, rarity: 0.02 },
    { id: 'quantumCore', name: 'Quantum Core', icon: '⚛️', minM: 17000, maxM: 22000, rarity: 0.015 },
    { id: 'infinityStar', name: 'Infinity Star', icon: '🌟', minM: 22000, maxM: 99999, rarity: 0.01 },
];

/* ══════════════════════════════════════════
   EPOCH SYSTEM — procedural milestones forever
   ══════════════════════════════════════════ */
const EPOCH_DEFS = [
    { m: 0, name: 'AWAKENING', desc: 'Your journey begins...', icon: '🌅' },
    { m: 2000, name: 'ASCENSION', desc: 'The sky is not the limit', icon: '🚀' },
    { m: 5000, name: 'TRANSCENDENCE', desc: 'Beyond mortal bounds', icon: '✨' },
    { m: 10000, name: 'INFINITY', desc: 'Limitless potential', icon: '♾️' },
    { m: 18000, name: 'GODHOOD', desc: 'You have ascended', icon: '👑' },
    { m: 30000, name: 'BEYOND', desc: 'Past the edge of reality', icon: '🌌' },
];

class EpochSystem {
    constructor() { this.currentEpoch = 0; this.lastMega = 0; }
    check(maxH) {
        // Fixed epoch milestones
        for (let i = this.currentEpoch + 1; i < EPOCH_DEFS.length; i++) {
            if (maxH >= EPOCH_DEFS[i].m) {
                this.currentEpoch = i;
                return { type: 'epoch', data: EPOCH_DEFS[i] };
            }
        }
        // Procedural mega-milestones every 5000m after last fixed epoch
        const lastFixed = EPOCH_DEFS[EPOCH_DEFS.length - 1].m;
        if (maxH > lastFixed) {
            const megaInterval = 5000;
            const megaCount = Math.floor((maxH - lastFixed) / megaInterval);
            if (megaCount > this.lastMega) {
                this.lastMega = megaCount;
                const totalM = lastFixed + megaCount * megaInterval;
                return {
                    type: 'mega',
                    data: {
                        m: totalM,
                        name: `LEVEL ${megaCount + EPOCH_DEFS.length}`,
                        desc: `${totalM}m — uncharted territory`,
                        icon: ['🔥', '💎', '⚡', '🌀', '🌟', '💫', '🪐', '✨'][megaCount % 8],
                    }
                };
            }
        }
        return null;
    }
    getName(maxH) {
        for (let i = EPOCH_DEFS.length - 1; i >= 0; i--) {
            if (maxH >= EPOCH_DEFS[i].m) return EPOCH_DEFS[i].name;
        }
        return EPOCH_DEFS[0].name;
    }
}

/* ══════════════════════════════════════════
   EVENT ENGINE
   ══════════════════════════════════════════ */
class EventEngine {
    constructor() { this.reset(); }
    reset() {
        this.active = null; this.timer = 0; this.cooldowns = {};
        this.sinceLastEvent = 0; this.history = [];
        this.guaranteeThreshold = 450;
    }
    update(maxH) {
        this.sinceLastEvent++;
        for (const k in this.cooldowns) if (this.cooldowns[k] > 0) this.cooldowns[k]--;
        if (this.active) {
            this.timer--;
            if (this.timer <= 0) { this.active = null; }
            return null;
        }
        const available = EVENT_DEFS.filter(e => maxH >= e.minM && (!this.cooldowns[e.id] || this.cooldowns[e.id] <= 0));
        if (available.length === 0) return null;
        let chance = 0.002 + clamp(this.sinceLastEvent / this.guaranteeThreshold, 0, 1) * 0.015;
        const recentIds = new Set(this.history.slice(-4));
        const novel = available.filter(e => !recentIds.has(e.id));
        if (this.sinceLastEvent > this.guaranteeThreshold) chance = 1;
        if (Math.random() < chance) {
            const pool = novel.length > 0 ? novel : available;
            const total = pool.reduce((s, e) => s + e.weight, 0);
            let r = Math.random() * total;
            let chosen = pool[0];
            for (const e of pool) { r -= e.weight; if (r <= 0) { chosen = e; break; } }
            this.active = chosen;
            this.timer = chosen.dur;
            this.cooldowns[chosen.id] = chosen.cooldown;
            this.sinceLastEvent = 0;
            this.history.push(chosen.id);
            if (this.history.length > 20) this.history.shift();
            return chosen;
        }
        return null;
    }
    is(id) { return this.active?.id === id; }
    progress() { return this.active ? 1 - this.timer / this.active.dur : 0; }
}

/* ══════════════════════════════════════════
   ENCOUNTER ENGINE
   ══════════════════════════════════════════ */
class EncounterEngine {
    constructor() { this.reset(); }
    reset() {
        this.active = null; this.cooldowns = {};
        this.sinceLastEncounter = 0; this.completed = 0;
    }
    tryStart(maxH) {
        this.sinceLastEncounter++;
        for (const k in this.cooldowns) if (this.cooldowns[k] > 0) this.cooldowns[k]--;
        if (this.active) return null;
        const available = ENCOUNTER_DEFS.filter(e => maxH >= e.minM && (!this.cooldowns[e.id] || this.cooldowns[e.id] <= 0));
        if (available.length === 0) return null;
        let chance = 0.0015 + clamp(this.sinceLastEncounter / 600, 0, 1) * 0.008;
        if (this.sinceLastEncounter > 700) chance = 0.4;
        if (Math.random() < chance) {
            const total = available.reduce((s, e) => s + e.weight, 0);
            let r = Math.random() * total, chosen = available[0];
            for (const e of available) { r -= e.weight; if (r <= 0) { chosen = e; break; } }
            this.active = chosen;
            this.cooldowns[chosen.id] = chosen.cooldown;
            this.sinceLastEncounter = 0;
            return chosen;
        }
        return null;
    }
    complete() { this.active = null; this.completed++; }
}

/* ══════════════════════════════════════════
   DIFFICULTY WAVE — logarithmic, no ceiling
   ══════════════════════════════════════════ */
class DifficultyWave {
    constructor() { this.phase = 0; this.intensity = 0; }
    update(maxH) {
        this.phase += 0.002 + maxH * 0.000002;
        const base = 0.3 + 0.7 * (1 - 1 / (1 + maxH / 5000));
        const wave = Math.sin(this.phase) * 0.15;
        const spike = Math.sin(this.phase * 3.7) > 0.9 ? 0.1 : 0;
        this.intensity = clamp(base + wave + spike, 0.05, 0.95);
    }
}

/* ══════════════════════════════════════════
   PARTICLE POOL
   ══════════════════════════════════════════ */
class ParticlePool {
    constructor(max) {
        this.max = max; this.pool = [];
        for (let i = 0; i < max; i++) this.pool.push({ active: false, x: 0, y: 0, vx: 0, vy: 0, life: 0, decay: 0, size: 0, color: '', grav: 0, shape: 0 });
    }
    spawn(x, y, count, color, mult = 1, shape = 0) {
        let spawned = 0;
        for (const p of this.pool) {
            if (p.active) continue;
            p.active = true; p.x = x; p.y = y;
            p.vx = (Math.random() * 6 - 3) * mult;
            p.vy = (Math.random() * -4 + 0.5) * mult;
            p.life = 1; p.decay = 0.02 + Math.random() * 0.025;
            p.size = 2 + Math.random() * 3; p.color = color; p.grav = 0.08; p.shape = shape;
            if (++spawned >= count) break;
        }
    }
    update() {
        for (const p of this.pool) {
            if (!p.active) continue;
            p.x += p.vx; p.y += p.vy; p.vy += p.grav; p.life -= p.decay;
            if (p.life <= 0) p.active = false;
        }
    }
    draw(ctx) {
        for (const p of this.pool) {
            if (!p.active) continue;
            ctx.globalAlpha = p.life;
            ctx.fillStyle = p.color;
            if (p.shape === 1) {
                ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.life * 3);
                const s = p.size * p.life;
                ctx.fillRect(-s / 2, -s / 2, s, s); ctx.restore();
            } else if (p.shape === 2) {
                ctx.beginPath();
                const s = p.size * p.life;
                ctx.moveTo(p.x, p.y - s); ctx.lineTo(p.x + s * 0.6, p.y + s * 0.5);
                ctx.lineTo(p.x - s * 0.6, p.y + s * 0.5); ctx.fill();
            } else {
                ctx.beginPath(); ctx.arc(p.x, p.y, p.size * p.life, 0, 6.283); ctx.fill();
            }
        }
        ctx.globalAlpha = 1;
    }
}

/* ══════════════════════════════════════════
   ENCOUNTER GENERATOR
   ══════════════════════════════════════════ */
class EncounterGen {
    constructor(w) { this.w = w; }
    generate(id, startY, w) {
        this.w = w;
        switch (id) {
            case 'staircase': return this._staircase(startY);
            case 'coinRing': return this._coinRing(startY);
            case 'zigzag': return this._zigzag(startY);
            case 'pyramid': return this._pyramid(startY);
            case 'corridor': return this._corridor(startY);
            case 'springChain': return this._springChain(startY);
            case 'coinWaterfall': return this._coinWaterfall(startY);
            case 'platformBridge': return this._platformBridge(startY);
            case 'funnel': return this._funnel(startY);
            case 'diamondPattern': return this._diamondPattern(startY);
            case 'helixRise': return this._helixRise(startY);
            case 'wavePath': return this._wavePath(startY);
            case 'galaxySpiral': return this._galaxySpiral(startY);
            case 'theArchitect': return this._theArchitect(startY);
            case 'quantumLeap': return this._quantumLeap(startY);
            default: return this._staircase(startY);
        }
    }

    _mp(x, y, type = PT.NORMAL, w = PLW) {
        return {
            x, y, w, h: PLH, type,
            hit: false, breaking: false, breakTimer: 0, breakPieces: null, crackAnim: 0,
            springAnim: 0,
            moveDir: Math.random() > 0.5 ? 1 : -1, moveSpd: rand(0.7, 1.6), moveRange: rand(30, 65), moveOX: x,
            ghostPh: rand(0, Math.PI * 2), ghostVis: true,
            convDir: Math.random() > 0.5 ? 1 : -1, convSpd: rand(1.5, 3),
            iceSlip: 0.96,
            teleportPairId: null, teleportFlash: 0,
            appearAnim: 1,
            chainTriggered: false,
            orbitCX: x + w / 2, orbitCY: y, orbitR: rand(20, 45), orbitSpd: rand(0.8, 2), orbitPh: rand(0, 6.28),
            invisAfterLand: false,
            eventHighlight: 0.8,
            magnetR: 80, magnetStr: 0.3,
            phasePh: rand(0, 6.28), phaseGroup: 0,
            splitDone: false,
            gravInvert: false, gravTimer: 0,
            trampolineNext: null,
            timedLife: 300, timedMax: 300,
        };
    }
    _mc(x, y) { return { x, y, w: COIN_SZ, h: COIN_SZ, collected: false, colAnim: 0, bob: rand(0, Math.PI * 2), valueMult: 1 }; }

    _staircase(startY) {
        const plats = [], coins = [];
        const steps = 8 + randInt(0, 4);
        let dir = Math.random() > 0.5 ? 1 : -1;
        let x = dir > 0 ? 30 : this.w - PLW - 30;
        let y = startY;
        for (let i = 0; i < steps; i++) {
            plats.push(this._mp(x, y));
            coins.push(this._mc(x + PLW / 2 - COIN_SZ / 2, y - 25));
            x += dir * (PLW * 0.7);
            y -= 55;
            if (x < 15 || x > this.w - PLW - 15) { dir *= -1; x = clamp(x, 15, this.w - PLW - 15); }
        }
        return { plats, coins, endY: y };
    }

    _coinRing(startY) {
        const plats = [], coins = [];
        const cx = this.w / 2, cy = startY - 120;
        const r = 60;
        for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            coins.push(this._mc(cx + Math.cos(angle) * r - COIN_SZ / 2, cy + Math.sin(angle) * r - COIN_SZ / 2));
        }
        plats.push(this._mp(cx - PLW / 2, startY));
        plats.push(this._mp(cx - PLW / 2, cy - r - 50));
        plats.push(this._mp(cx - PLW / 2 - 50, cy));
        plats.push(this._mp(cx - PLW / 2 + 50, cy));
        return { plats, coins, endY: cy - r - 50 };
    }

    _zigzag(startY) {
        const plats = [], coins = [];
        const steps = 10;
        let y = startY;
        const dy = 55;
        const sideMargin = 25;
        const effectiveW = Math.min(this.w - PLW - sideMargin * 2, 180);
        const startX = (this.w - effectiveW - PLW) / 2;
        for (let i = 0; i < steps; i++) {
            const x = (i % 2 === 0) ? startX : startX + effectiveW;
            plats.push(this._mp(x, y));
            if (i < steps - 1) coins.push(this._mc(this.w / 2 - COIN_SZ / 2, y - 28));
            y -= dy;
        }
        return { plats, coins, endY: y };
    }

    _pyramid(startY) {
        const plats = [], coins = [];
        const rows = 5;
        let y = startY;
        for (let row = rows; row >= 1; row--) {
            const totalW = row * (PLW + 8);
            const sx = (this.w - totalW) / 2;
            for (let col = 0; col < row; col++) plats.push(this._mp(sx + col * (PLW + 8), y));
            if (row <= 3) coins.push(this._mc(this.w / 2 - COIN_SZ / 2, y - 25));
            y -= 55;
        }
        return { plats, coins, endY: y };
    }

    _corridor(startY) {
        const plats = [], coins = [];
        const steps = 8;
        let y = startY;
        const cx = this.w / 2, hw = 45;
        for (let i = 0; i < steps; i++) {
            const x = clamp(cx + rand(-hw, hw) - PLW / 2, 15, this.w - PLW - 15);
            plats.push(this._mp(x, y));
            coins.push(this._mc(cx - COIN_SZ / 2, y - 28));
            y -= 52;
        }
        return { plats, coins, endY: y };
    }

    _springChain(startY) {
        const plats = [], coins = [];
        const count = 4;
        let y = startY;
        for (let i = 0; i < count; i++) {
            const x = rand(30, this.w - PLW - 30);
            plats.push(this._mp(x, y, PT.SPRING));
            for (let j = 0; j < 3; j++) coins.push(this._mc(x + PLW / 2 - COIN_SZ / 2 + (j - 1) * 18, y - 30 - j * 15));
            y -= 110;
        }
        return { plats, coins, endY: y };
    }

    _coinWaterfall(startY) {
        const plats = [], coins = [];
        const cx = this.w / 2;
        for (let i = 0; i < 15; i++) coins.push(this._mc(cx + Math.sin(i * 0.5) * 40 - COIN_SZ / 2, startY - 20 * i));
        plats.push(this._mp(cx - PLW / 2, startY));
        plats.push(this._mp(cx - PLW / 2 - 40, startY - 150));
        plats.push(this._mp(cx - PLW / 2 + 40, startY - 300));
        return { plats, coins, endY: startY - 320 };
    }

    _platformBridge(startY) {
        const plats = [], coins = [];
        const count = 6;
        const gap = PLW + 5;
        const totalW = count * gap;
        const sx = (this.w - totalW) / 2;
        for (let i = 0; i < count; i++) plats.push(this._mp(sx + i * gap, startY));
        for (let i = 0; i < count - 1; i++) coins.push(this._mc(sx + i * gap + PLW / 2, startY - 25));
        plats.push(this._mp(this.w / 2 - PLW / 2, startY - 80));
        return { plats, coins, endY: startY - 80 };
    }

    _funnel(startY) {
        const plats = [], coins = [];
        const steps = 10;
        let y = startY;
        for (let i = 0; i < steps; i++) {
            const width = lerp(this.w - 60, 80, i / steps);
            const cx = this.w / 2;
            const x = clamp(cx - width / 2 + rand(-10, 10), 15, this.w - PLW - 15);
            plats.push(this._mp(x, y));
            if (i % 2 === 0) coins.push(this._mc(cx - COIN_SZ / 2, y - 25));
            y -= 52;
        }
        return { plats, coins, endY: y };
    }

    _diamondPattern(startY) {
        const plats = [], coins = [];
        const cx = this.w / 2;
        const positions = [[0, 0], [-60, -50], [60, -50], [-90, -100], [0, -100], [90, -100], [-60, -150], [60, -150], [0, -200]];
        for (const [dx, dy] of positions) plats.push(this._mp(clamp(cx + dx - PLW / 2, 10, this.w - PLW - 10), startY + dy));
        coins.push(this._mc(cx - COIN_SZ / 2, startY - 100));
        coins.push(this._mc(cx - COIN_SZ / 2, startY - 60));
        coins.push(this._mc(cx - COIN_SZ / 2, startY - 140));
        return { plats, coins, endY: startY - 220 };
    }

    _helixRise(startY) {
        const plats = [], coins = [];
        const steps = 14;
        let y = startY;
        const maxA = Math.min(this.w * 0.3, 100);
        for (let i = 0; i < steps; i++) {
            const angle = (i / steps) * Math.PI * 3;
            const x = clamp(this.w / 2 + Math.sin(angle) * maxA - PLW / 2, 10, this.w - PLW - 10);
            plats.push(this._mp(x, y));
            if (i % 2 === 0) coins.push(this._mc(clamp(x + PLW / 2 - COIN_SZ / 2, 5, this.w - COIN_SZ - 5), y - 25));
            y -= 48;
        }
        return { plats, coins, endY: y };
    }

    _wavePath(startY) {
        const plats = [], coins = [];
        const steps = 12;
        let y = startY;
        const maxA = Math.min(this.w * 0.3, 110);
        for (let i = 0; i < steps; i++) {
            const x = clamp(this.w / 2 + Math.sin(i * 0.8) * maxA - PLW / 2, 10, this.w - PLW - 10);
            plats.push(this._mp(x, y));
            coins.push(this._mc(clamp(x + PLW / 2 - COIN_SZ / 2, 5, this.w - COIN_SZ - 5), y - 22));
            y -= 50;
        }
        return { plats, coins, endY: y };
    }

    _galaxySpiral(startY) {
        const plats = [], coins = [];
        const steps = 18;
        let y = startY;
        const cx = this.w / 2;
        const maxR = Math.min(this.w * 0.22, 120);
        for (let i = 0; i < steps; i++) {
            const angle = (i / steps) * Math.PI * 4;
            const r = 25 + (i / steps) * maxR;
            const x = clamp(cx + Math.cos(angle) * r - PLW / 2, 10, this.w - PLW - 10);
            plats.push(this._mp(x, y, i % 5 === 0 ? PT.SPRING : PT.NORMAL));
            if (i % 3 === 0) {
                for (let j = 0; j < 3; j++) {
                    const ca = angle + (j - 1) * 0.3;
                    coins.push(this._mc(clamp(cx + Math.cos(ca) * (r + 15) - COIN_SZ / 2, 5, this.w - COIN_SZ - 5), y - 15));
                }
            }
            y -= 46;
        }
        return { plats, coins, endY: y };
    }

    _theArchitect(startY) {
        const plats = [], coins = [];
        let y = startY;
        const cx = this.w / 2;
        const offsets = [[0, 0], [-60, -52], [60, -52], [0, -104], [-60, -156], [60, -156], [0, -208]];
        for (const [dx, dy] of offsets) {
            plats.push(this._mp(clamp(cx + dx - PLW / 2, 10, this.w - PLW - 10), y + dy));
            coins.push(this._mc(clamp(cx + dx - COIN_SZ / 2, 5, this.w - COIN_SZ - 5), y + dy - 22));
        }
        return { plats, coins, endY: y - 230 };
    }

    _quantumLeap(startY) {
        const plats = [], coins = [];
        let y = startY;
        let lastX = rand(20, this.w - PLW - 20);
        for (let i = 0; i < 6; i++) {
            const maxJX = 200;
            const x = clamp(rand(lastX - maxJX, lastX + maxJX), 20, this.w - PLW - 20);
            lastX = x;
            plats.push(this._mp(x, y, PT.SPRING));
            for (let j = 0; j < 5; j++) coins.push(this._mc(x + PLW / 2 - COIN_SZ / 2 + (j - 2) * 16, y - 40 - j * 20));
            y -= 160;
        }
        return { plats, coins, endY: y };
    }
}

/* ══════════════════════════════════════════
   GENERATOR — with reachability guarantee
   ══════════════════════════════════════════ */
class Generator {
    constructor(w) {
        this.w = w; this.diff = 0; this.height = 0;
        this.last = [];
        this.unlockedPlats = new Set([PT.NORMAL]);
        this.unlockedPU = new Set();
        this.unlockedHazards = new Set();
        this.teleportPairPending = null;
    }
    reachable(from, toX, toY) {
        const fcx = from.x + from.w / 2, tcx = toX + PLW / 2;
        const dy = from.y - toY, dx = Math.abs(fcx - tcx);
        if (dy < 0) return true;
        let boost = 1;
        if (from.type === PT.SPRING || from.type === PT.TRAMPOLINE_CHAIN) boost = 1.7;
        if (from.type === PT.CONVEYOR) boost = 1.3;
        const v0 = Math.abs(JF * boost);
        const maxH = (v0 * v0) / (2 * G);
        if (dy > maxH * 0.82) return false;
        const disc = v0 * v0 - 2 * G * dy;
        if (disc < 0) return false;
        const tUp = (v0 - Math.sqrt(disc)) / G;
        return dx < MS * tUp * 1.8 + PLW * 0.7;
    }

    reachableFromAny(x, y) {
        for (const p of this.last) if (this.reachable(p, x, y)) return true;
        return false;
    }

    findX(y, constraints) {
        const m = 14;
        let minX = m, maxX = this.w - PLW - m;
        if (constraints) {
            minX = Math.max(m, constraints.minX || m);
            maxX = Math.min(this.w - PLW - m, constraints.maxX || this.w - PLW - m);
        }
        for (let i = 0; i < 15; i++) {
            const x = rand(minX, maxX);
            if (this.reachableFromAny(x, y)) return x;
        }
        if (this.last.length > 0) {
            const src = this.last.reduce((b, p) => p.y < b.y ? p : b, this.last[0]);
            const dy = src.y - y;
            const v0 = Math.abs((src.type === PT.SPRING || src.type === PT.TRAMPOLINE_CHAIN) ? SJF : JF);
            const disc = v0 * v0 - 2 * G * dy;
            const tUp = disc > 0 ? (v0 - Math.sqrt(disc)) / G : v0 / G;
            const maxReach = MS * tUp * 1.6 + PLW * 0.4;
            const baseX = src.x + src.w / 2;
            return clamp(baseX + rand(-maxReach, maxReach) - PLW / 2, minX, maxX);
        }
        return rand(minX, maxX);
    }

    chooseType(hasSafe, eventOverride) {
        if (eventOverride === 'bouncy') return PT.SPRING;
        if (eventOverride === 'safe') return PT.NORMAL;
        const d = this.diff;
        const types = [PT.NORMAL], weights = [60 - d * 20];
        const addIf = (pt, w) => { if (this.unlockedPlats.has(pt)) { types.push(pt); weights.push(Math.max(0, w)); } };
        addIf(PT.MOVING, 8 + d * 12);
        addIf(PT.SPRING, 3 + d * 5);
        addIf(PT.GHOST, 4 + d * 8);
        addIf(PT.TELEPORT, 2 + d * 3);
        addIf(PT.CONVEYOR, 4 + d * 6);
        addIf(PT.ICE, 5 + d * 8);
        addIf(PT.CRUMBLE_CHAIN, 2 + d * 4);
        addIf(PT.ORBITING, 2 + d * 3);
        addIf(PT.YOURSIZE, 2 + d * 3);
        addIf(PT.MAGNET_PLAT, 3 + d * 4);
        addIf(PT.SPLIT, 2 + d * 3);
        addIf(PT.PHASE, 2 + d * 3);
        addIf(PT.TRAMPOLINE_CHAIN, 2 + d * 3);
        addIf(PT.GRAVITY_PLAT, 1 + d * 2);
        addIf(PT.TIMED, 2 + d * 4);
        if (hasSafe) addIf(PT.BREAKING, 7 + d * 14);
        const total = weights.reduce((a, b) => a + b, 0);
        let r = Math.random() * total;
        for (let i = 0; i < types.length; i++) { r -= weights[i]; if (r <= 0) return types[i]; }
        return PT.NORMAL;
    }

    generate(fromY, toY, eventCtx, widePlatMod = 1) {
        const plats = [], coins = [], pus = [], hazards = [];
        let y = fromY;
        const gapMod = eventCtx?.gapMod || 1;
        const constraints = eventCtx?.constraints || null;
        const eventPlatOverride = eventCtx?.platOverride || null;
        const coinMult = eventCtx?.coinMult || 1;
        const puChanceMult = eventCtx?.puChanceMult || 1;
        const spiralMode = eventCtx?.spiral || false;

        while (y > toY) {
            const lastPlat = plats.length > 0 ? plats[plats.length - 1] : (this.last.length > 0 ? this.last[this.last.length - 1] : null);
            let curMinG = 42 * gapMod;
            let curMaxG = lerp(75, 108, this.diff) * gapMod;
            if (lastPlat?.type === PT.SPRING || lastPlat?.type === PT.TRAMPOLINE_CHAIN) curMaxG *= 1.5;
            if (lastPlat?.type === PT.CONVEYOR) curMaxG *= 1.2;
            y -= rand(curMinG, curMaxG);

            let x;
            if (spiralMode) {
                const cx = this.w / 2 + Math.sin(y * 0.015) * (this.w * 0.25);
                x = clamp(cx - PLW / 2, 14, this.w - PLW - 14);
            } else {
                x = this.findX(y, constraints);
            }

            const nearSafe = this.last.some(p => Math.abs(p.y - y) < 55 && p.type !== PT.BREAKING) ||
                plats.some(p => Math.abs(p.y - y) < 55 && p.type !== PT.BREAKING);
            let type = this.chooseType(nearSafe, eventPlatOverride);
            if ((type === PT.BREAKING || type === PT.CRUMBLE_CHAIN) && !nearSafe) type = PT.NORMAL;

            if (type === PT.TELEPORT) {
                if (this.teleportPairPending) {
                    const pl = this._mkPlat(x, y, PT.TELEPORT, widePlatMod);
                    pl.teleportPairId = this.teleportPairPending;
                    plats.push(pl); this.teleportPairPending = null;
                } else {
                    const id = Math.random().toString(36).substr(2, 6);
                    const pl = this._mkPlat(x, y, PT.TELEPORT, widePlatMod);
                    pl.teleportPairId = id; plats.push(pl);
                    this.teleportPairPending = id;
                }
            } else if (type === PT.TRAMPOLINE_CHAIN) {
                const chainLen = randInt(2, 3);
                for (let i = 0; i < chainLen; i++) {
                    const cx = this.findX(y, constraints);
                    const pl = this._mkPlat(cx, y, PT.TRAMPOLINE_CHAIN, widePlatMod);
                    plats.push(pl);
                    this.last.push(pl);
                    if (this.last.length > 6) this.last.shift();
                    y -= rand(80, 110);
                }
                continue;
            } else {
                if (this.teleportPairPending) {
                    const pl = this._mkPlat(x, y, PT.TELEPORT, widePlatMod);
                    pl.teleportPairId = this.teleportPairPending;
                    plats.push(pl); this.teleportPairPending = null;
                } else {
                    plats.push(this._mkPlat(x, y, type, widePlatMod));
                }
            }

            const pl = plats[plats.length - 1];
            this.last.push(pl);
            if (this.last.length > 6) this.last.shift();

            if (pl.type !== PT.BREAKING && Math.random() < 0.2 * coinMult) {
                coins.push(this._mkCoin(x + PLW / 2 - COIN_SZ / 2 + rand(-12, 12), y - rand(25, 38)));
            }
            if (Math.random() < 0.06 * coinMult && pl.type === PT.NORMAL) {
                const n = 3 + Math.floor(Math.random() * 3);
                for (let i = 0; i < n; i++) {
                    coins.push(this._mkCoin(x + PLW / 2 - COIN_SZ / 2 + (i - n / 2) * 20, y - 32 - Math.sin((i / (n - 1)) * Math.PI) * 22));
                }
            }
            if (Math.random() < 0.03 * puChanceMult && (pl.type === PT.NORMAL || pl.type === PT.MOVING)) {
                const available = [...this.unlockedPU];
                if (available.length > 0) pus.push(this._mkPU(x + PLW / 2 - PU_SZ / 2, y - 28, available[Math.floor(Math.random() * available.length)]));
            }
        }

        if (this.unlockedHazards.has('rocket') && Math.random() < 0.012 + this.diff * 0.012) {
            hazards.push(this._mkRocket(rand(fromY - 100, toY)));
        }
        if (this.unlockedHazards.has('blackhole') && Math.random() < 0.006 + this.diff * 0.006) {
            hazards.push(this._mkBlackhole(rand(50, this.w - 50), rand(fromY - 80, toY + 80)));
        }
        if (this.unlockedHazards.has('homingMine') && Math.random() < 0.004 + this.diff * 0.003) {
            hazards.push(this._mkHomingMine(rand(30, this.w - 30), rand(fromY - 50, toY + 50)));
        }

        return { plats, coins, pus, hazards };
    }

    _mkPlat(x, y, type, wideMod = 1) {
        const baseW = type === PT.YOURSIZE ? rand(40, 100) : PLW;
        const w = baseW * wideMod;
        return {
            x, y, w, h: PLH, type,
            hit: false, breaking: false, breakTimer: 0, breakPieces: null, crackAnim: 0,
            springAnim: 0,
            moveDir: Math.random() > 0.5 ? 1 : -1, moveSpd: rand(0.7, 1.6), moveRange: rand(30, 65), moveOX: x,
            ghostPh: rand(0, Math.PI * 2), ghostVis: true,
            convDir: Math.random() > 0.5 ? 1 : -1, convSpd: rand(1.5, 3),
            iceSlip: 0.96,
            teleportPairId: null, teleportFlash: 0,
            appearAnim: 1,
            chainTriggered: false,
            orbitCX: x + w / 2, orbitCY: y, orbitR: rand(20, 45), orbitSpd: rand(0.8, 2), orbitPh: rand(0, 6.28),
            invisAfterLand: false, eventHighlight: 0,
            magnetR: 80, magnetStr: 0.3,
            phasePh: rand(0, 6.28), phaseGroup: randInt(0, 1),
            splitDone: false,
            gravInvert: false, gravTimer: 120,
            trampolineNext: null,
            timedLife: 300 + randInt(0, 100), timedMax: 400,
        };
    }
    _mkCoin(x, y) { return { x, y, w: COIN_SZ, h: COIN_SZ, collected: false, colAnim: 0, bob: rand(0, Math.PI * 2), valueMult: 1 }; }
    _mkPU(x, y, type) { return { x, y, w: PU_SZ, h: PU_SZ, type, collected: false, colAnim: 0, bob: rand(0, Math.PI * 2) }; }
    _mkRocket(y) {
        const fromLeft = Math.random() > 0.5;
        return { kind: 'rocket', x: fromLeft ? -40 : 0, y, w: 36, h: 12, vx: fromLeft ? 3 + rand(0, 2) : -(3 + rand(0, 2)), fromLeft, active: true, warn: 60, warnDone: false };
    }
    _mkBlackhole(x, y) { return { kind: 'blackhole', x, y, r: 22, active: true, life: 400 + rand(0, 200), phase: rand(0, Math.PI * 2) }; }
    _mkMeteor(w) { return { kind: 'meteor', x: rand(10, w - 10), y: 0, vy: 4 + rand(0, 4), vx: rand(-1, 1), r: rand(4, 10), active: true, trail: [] }; }
    _mkHomingMine(x, y) { return { kind: 'homingMine', x, y, vx: 0, vy: 0, active: true, life: 500 + rand(0, 200), r: 8, trackStr: 0.02 }; }
}

/* ══════════════════════════════════════════
   BACKGROUND RENDERER
   ══════════════════════════════════════════ */
class BgRenderer {
    constructor(w, h) {
        this.w = w; this.h = h;
        this.stars = [];
        for (let i = 0; i < 45; i++) this.stars.push({ x: rand(0, w), y: rand(0, h * 5), sz: rand(0.5, 2), a: rand(0.1, 0.5), sp: rand(0.01, 0.025), off: rand(0, 6.28) });
        this.neonSigns = [];
        for (let i = 0; i < 8; i++) this.neonSigns.push({ x: rand(5, w - 30), yOff: rand(0, 800), w: rand(25, 60), h: rand(8, 20), hue: rand(0, 360), flicker: rand(0.5, 3) });
        this.snowflakes = [];
        for (let i = 0; i < 30; i++) this.snowflakes.push({ x: rand(0, w), yOff: rand(0, 600), sz: rand(1, 3), sp: rand(0.3, 1), drift: rand(-0.5, 0.5), off: rand(0, 6.28) });
        this.rainColumns = [];
        for (let i = 0; i < 20; i++) this.rainColumns.push({ x: rand(0, w), sp: rand(2, 6) });
    }
    draw(ctx, camY, biome, time) {
        for (const s of this.stars) {
            const sy = ((s.y - camY * 0.15) % (this.h * 5) + this.h * 5) % (this.h * 5) - this.h * 0.3;
            if (sy < -3 || sy > this.h + 3) continue;
            ctx.globalAlpha = s.a * (0.5 + 0.5 * Math.sin(time * s.sp * 60 + s.off));
            ctx.fillStyle = biome.starColor;
            ctx.beginPath(); ctx.arc(s.x, sy, s.sz, 0, 6.283); ctx.fill();
        }
        ctx.globalAlpha = 1;
        const el = biome.bgElements;
        if (el === 'neonSigns') this._neon(ctx, camY, time, biome);
        else if (el === 'embers' || el === 'goldenDust') this._embers(ctx, camY, time, biome);
        else if (el === 'snowflakes') this._snow(ctx, camY, time);
        else if (el === 'digitalRain') this._rain(ctx, camY, time, biome);
        else if (el === 'voidPulse' || el === 'chaosField') this._void(ctx, time, biome);
        else if (el === 'bloodRain') this._bloodRain(ctx, time, biome);
        else if (el === 'aurora') this._aurora(ctx, time, biome);
        else if (el === 'nebula') this._nebula(ctx, time, biome);
        else if (el === 'quantum') this._quantum(ctx, time, biome);
    }
    _neon(ctx, camY, time, biome) {
        for (const s of this.neonSigns) {
            const y = ((s.yOff - camY * 0.3) % 800 + 800) % 800 - 100;
            if (y < -30 || y > this.h + 30) continue;
            const flick = Math.sin(time * s.flicker) > -0.2 ? 1 : 0.15;
            ctx.globalAlpha = 0.15 * flick;
            ctx.fillStyle = `hsl(${s.hue},80%,60%)`;
            ctx.shadowColor = `hsl(${s.hue},80%,60%)`; ctx.shadowBlur = 12;
            ctx.fillRect(s.x, y, s.w, s.h); ctx.shadowBlur = 0;
        }
        ctx.globalAlpha = 1;
    }
    _embers(ctx, camY, time, biome) {
        for (let i = 0; i < 15; i++) {
            const x = (Math.sin(time * 0.5 + i * 1.7) * 0.5 + 0.5) * this.w;
            const y = ((i * 120 + time * 30) % (this.h + 100)) - 50;
            ctx.globalAlpha = 0.3 + 0.3 * Math.sin(time * 3 + i);
            ctx.fillStyle = i % 3 === 0 ? biome.starColor : biome.particleColor;
            ctx.beginPath(); ctx.arc(x, y, 1.5 + Math.sin(time * 2 + i) * 0.5, 0, 6.283); ctx.fill();
        }
        ctx.globalAlpha = 1;
    }
    _snow(ctx, camY, time) {
        for (const s of this.snowflakes) {
            const y = ((s.yOff + time * s.sp * 30) % (this.h + 100)) - 50;
            const x = s.x + Math.sin(time * 0.8 + s.off) * 20;
            ctx.globalAlpha = 0.4; ctx.fillStyle = '#e0f2fe';
            ctx.beginPath(); ctx.arc(x, y, s.sz, 0, 6.283); ctx.fill();
        }
        ctx.globalAlpha = 1;
    }
    _rain(ctx, camY, time, biome) {
        ctx.font = '10px monospace'; ctx.fillStyle = biome.accent;
        for (const col of this.rainColumns) {
            const fy = ((time * col.sp * 15) % (this.h + 200)) - 100;
            for (let j = 0; j < 8; j++) {
                const cy = fy - j * 14;
                if (cy < -10 || cy > this.h + 10) continue;
                ctx.globalAlpha = (0.3 - j * 0.035) * (j === 0 ? 1 : 0.5);
                ctx.fillText(String.fromCharCode(0x30A0 + Math.floor(Math.random() * 96)), col.x, cy);
            }
        }
        ctx.globalAlpha = 1;
    }
    _void(ctx, time, biome) {
        const pulse = 0.5 + 0.5 * Math.sin(time * 0.8);
        const r = 80 + pulse * 40;
        const grad = ctx.createRadialGradient(this.w / 2, this.h / 2, 0, this.w / 2, this.h / 2, r);
        grad.addColorStop(0, biome.accent + '15'); grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad; ctx.fillRect(0, 0, this.w, this.h);
    }
    _bloodRain(ctx, time, biome) {
        for (let i = 0; i < 12; i++) {
            const x = (i * 31 + time * 20) % this.w;
            const y = ((time * (40 + i * 5)) % (this.h + 60)) - 30;
            ctx.globalAlpha = 0.2; ctx.fillStyle = biome.particleColor;
            ctx.fillRect(x, y, 1, 8 + i * 0.5);
        }
        ctx.globalAlpha = 1;
    }
    _aurora(ctx, time, biome) {
        for (let i = 0; i < 3; i++) {
            const y = this.h * 0.2 + Math.sin(time * 0.3 + i * 2) * 40;
            const grad = ctx.createLinearGradient(0, y - 30, 0, y + 30);
            grad.addColorStop(0, 'transparent'); grad.addColorStop(0.5, biome.accent + '12'); grad.addColorStop(1, 'transparent');
            ctx.fillStyle = grad; ctx.fillRect(0, y - 30, this.w, 60);
        }
    }
    _nebula(ctx, time, biome) {
        for (let i = 0; i < 5; i++) {
            const x = this.w * (0.2 + i * 0.15) + Math.sin(time * 0.2 + i) * 30;
            const y = this.h * (0.3 + Math.sin(time * 0.15 + i * 1.5) * 0.2);
            const r = 40 + Math.sin(time * 0.4 + i) * 15;
            const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
            const hue = (time * 20 + i * 60) % 360;
            grad.addColorStop(0, `hsla(${hue},60%,50%,0.08)`); grad.addColorStop(1, 'transparent');
            ctx.fillStyle = grad;
            ctx.beginPath(); ctx.arc(x, y, r, 0, 6.283); ctx.fill();
        }
    }
    _quantum(ctx, time, biome) {
        ctx.strokeStyle = biome.accent + '08'; ctx.lineWidth = 0.5;
        const gridSize = 40;
        const offset = (time * 10) % gridSize;
        for (let x = -offset; x < this.w + gridSize; x += gridSize) {
            ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, this.h); ctx.stroke();
        }
        for (let y = -offset; y < this.h + gridSize; y += gridSize) {
            ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(this.w, y); ctx.stroke();
        }
        for (let i = 0; i < 8; i++) {
            const px = (Math.sin(time * 0.7 + i * 2.3) * 0.5 + 0.5) * this.w;
            const py = (Math.cos(time * 0.5 + i * 1.7) * 0.5 + 0.5) * this.h;
            ctx.globalAlpha = 0.2 + Math.sin(time * 3 + i) * 0.1;
            ctx.fillStyle = biome.particleColor;
            ctx.beginPath(); ctx.arc(px, py, 2, 0, 6.283); ctx.fill();
        }
        ctx.globalAlpha = 1;
    }
}

/* ══════════════════════════════════════════
   MAIN GAME CLASS
   ══════════════════════════════════════════ */
class DoodleGame {
    constructor(canvas, baseTheme, sound) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.baseTheme = baseTheme;
        this.sound = sound;
        this.dpr = Math.min(window.devicePixelRatio || 1, 2);
        this.w = 0; this.h = 0;
        this.resize();
        this.keys = { left: false, right: false };
        this.touchX = null;
        this.gen = new Generator(this.w);
        this.particles = new ParticlePool(200);
        this.bg = new BgRenderer(this.w, this.h);
        this.events = new EventEngine();
        this.encounters = new EncounterEngine();
        this.encounterGen = new EncounterGen(this.w);
        this.diffWave = new DifficultyWave();
        this.comboEvents = new ComboEventEngine();
        this.epochSystem = new EpochSystem();
        this.reset();
    }
    resize() {
        const r = this.canvas.parentElement?.getBoundingClientRect();
        if (!r) return;
        this.w = r.width; this.h = r.height;
        this.canvas.width = this.w * this.dpr;
        this.canvas.height = this.h * this.dpr;
        this.canvas.style.width = this.w + 'px';
        this.canvas.style.height = this.h + 'px';
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        if (this.gen) this.gen.w = this.w;
        if (this.bg) { this.bg.w = this.w; this.bg.h = this.h; }
        if (this.encounterGen) this.encounterGen.w = this.w;
    }

    reset() {
        this.score = 0; this.maxH = 0; this.over = false; this.overT = 0;
        this.scoreSubmitted = false;
        this.camY = 0; this.targetCamY = 0;
        this.shakeX = 0; this.shakeY = 0; this.shakeD = 0;
        this.lastMile = 0; this.diff = 0; this.frame = 0;
        this.combo = 0; this.comboT = 0; this.maxCombo = 0;
        this.currentBiome = BIOMES[0]; this.biomeTransition = 0; this.prevBiome = null;
        this.unlockQueue = []; this.unlockDisplay = null; this.unlockDisplayT = 0;
        this.teaserDisplay = null; this.teaserT = 0;
        this.eventAnnounce = null; this.eventAnnounceT = 0;
        this.encounterAnnounce = null; this.encounterAnnounceT = 0;
        this.encounterActive = false; this.encounterEndY = 0;
        this.comboEventAnnounce = null; this.comboEventAnnounceT = 0;
        this.epochAnnounce = null; this.epochAnnounceT = 0;

        this.meteors = [];
        this.gravMod = 1;
        this.timeMod = 1;
        this.playerScale = 1;
        this.targetPlayerScale = 1;
        this.zoomLevel = 1; this.targetZoom = 1;

        this.phantom = null;

        this.coinsCollected = 0;
        this.usedShield = false;
        this.biomesVisited = new Set([BIOMES[0].name]);
        this.eventsSurvived = 0;
        this.jumpCount = 0;
        this.nearMissCount = 0;
        this.nearMissFlash = 0;
        this.reviveUsed = false;

        this.activeMutators = [];
        this.nextMutatorM = 3000;
        this.mutatorAnnounce = null; this.mutatorAnnounceT = 0;

        this.achievementQueue = [];
        this.achievementDisplay = null; this.achievementDisplayT = 0;
        this.unlockedAchievements = this._loadAchievements();

        this.collectedArtifacts = this._loadArtifacts();
        this.artifactDisplay = null; this.artifactDisplayT = 0;

        this.doubleJumpAvail = false;
        this.doubleJumpUsed = false;

        // Combo event modifiers
        this._comboWideMod = 1;
        this._comboPlatOverride = null;
        this._comboGapMod = 1;
        this._comboCoinMult = 1;
        this._comboSpiral = false;

        // Float text throttle
        this._floatCount = 0;
        this._floatResetFrame = 0;

        this.p = {
            x: this.w / 2 - PW / 2, y: this.h - 100,
            vx: 0, vy: 0, w: PW, h: PH,
            sq: 1, sqT: 1, faceR: true, trail: [], jAnim: 0,
            shield: false, shieldT: 0, shieldBreak: 0,
            jet: false, jetFuel: 0,
            magnet: false, magnetT: 0,
            onIce: false,
            timeSlow: false, timeSlowT: 0,
            tiny: false, tinyT: 0,
            giant: false, giantT: 0,
            gravInverted: false, gravInvertT: 0,
        };

        this.plats = []; this.coins = []; this.pus = []; this.hazards = [];
        this.floats = [];
        this.artifacts = [];

        this.gen.diff = 0; this.gen.height = 0; this.gen.last = [];
        this.gen.unlockedPlats = new Set([PT.NORMAL]);
        this.gen.unlockedPU = new Set();
        this.gen.unlockedHazards = new Set();
        this.gen.teleportPairPending = null;

        this.events.reset();
        this.encounters.reset();
        this.comboEvents.reset();
        this.epochSystem = new EpochSystem();
        this.diffWave = new DifficultyWave();

        const ground = this.gen._mkPlat(this.w / 2 - PLW / 2, this.h - 50, PT.NORMAL);
        ground.appearAnim = 0;
        this.plats.push(ground);
        this.gen.last = [ground];

        const { plats, coins, pus, hazards } = this.gen.generate(this.h - 50, -300, null);
        this.plats.push(...plats); this.coins.push(...coins); this.pus.push(...pus); this.hazards.push(...hazards);
    }

    shake(n) { this.shakeD = Math.max(this.shakeD, n); }

    _float(x, y, text, color, big = false) {
        // Throttle float texts to prevent lag
        if (this.frame - this._floatResetFrame > 30) {
            this._floatCount = 0;
            this._floatResetFrame = this.frame;
        }
        if (this._floatCount > 8) return;
        this._floatCount++;
        this.floats.push({ x, y, text, color, life: 1, vy: -1.6, big });
    }

    hasMutator(id) { return this.activeMutators.some(m => m.id === id); }

    _checkUnlocks() {
        for (const u of UNLOCKS) {
            if (this.maxH >= u.m) {
                if (u.type === 'plat' && !this.gen.unlockedPlats.has(u.plat)) { this.gen.unlockedPlats.add(u.plat); this._showUnlock(u); }
                if (u.type === 'pu' && !this.gen.unlockedPU.has(u.pu)) { this.gen.unlockedPU.add(u.pu); this._showUnlock(u); }
                if (u.type === 'hazard' && !this.gen.unlockedHazards.has(u.hazard)) { this.gen.unlockedHazards.add(u.hazard); this._showUnlock(u); }
            }
        }
        for (const u of UNLOCKS) {
            const teaserM = u.m - 50;
            if (this.maxH >= teaserM && this.maxH < u.m && !this.teaserDisplay) {
                const already = (u.type === 'plat' && this.gen.unlockedPlats.has(u.plat)) ||
                    (u.type === 'pu' && this.gen.unlockedPU.has(u.pu)) ||
                    (u.type === 'hazard' && this.gen.unlockedHazards.has(u.hazard));
                if (!already) { this.teaserDisplay = { text: `??? unlocks at ${u.m}m`, icon: '❓' }; this.teaserT = 180; }
            }
        }
    }

    _showUnlock(u) { this.unlockDisplay = u; this.unlockDisplayT = 200; this.sound.unlock(); this.shake(3); }

    _checkBiome() {
        // Check hardcoded biomes first
        let newBiome = BIOMES.find(b => this.maxH >= b.startM && this.maxH < b.endM);

        // If beyond last hardcoded biome, use procedural
        if (!newBiome) {
            const proc = getProceduralBiome(this.maxH);
            if (proc) newBiome = proc;
            else newBiome = BIOMES[BIOMES.length - 1];
        }

        if (newBiome.name !== this.currentBiome.name) {
            this.prevBiome = this.currentBiome;
            this.currentBiome = newBiome;
            this.biomeTransition = 1;
            this.sound.biomeChange();
            this._float(this.w / 2, this.p.y - 70, `⬡ ${newBiome.name} ⬡`, newBiome.accent, true);
            this.biomesVisited.add(newBiome.name);
        }
        if (this.biomeTransition > 0) this.biomeTransition = Math.max(0, this.biomeTransition - 0.008);
    }

    _checkMutators() {
        if (this.maxH >= this.nextMutatorM) {
            const available = MUTATOR_DEFS.filter(m => this.maxH >= m.minM && !this.hasMutator(m.id));
            if (available.length > 0) {
                const mut = available[randInt(0, available.length - 1)];
                this.activeMutators.push(mut);
                this.mutatorAnnounce = mut;
                this.mutatorAnnounceT = 180;
                this.sound.megaEvent();
                this.shake(5);
                this._float(this.w / 2, this.p.y - 80, `🔥 ${mut.name}`, '#fbbf24', true);
            }
            this.nextMutatorM += 3000;
        }
    }

    _checkEpochs() {
        const result = this.epochSystem.check(this.maxH);
        if (result) {
            this.epochAnnounce = result.data;
            this.epochAnnounceT = 250;
            this.sound.epoch();
            this.shake(6);
            this.particles.spawn(this.w / 2, this.p.y - 40, 25, this.currentBiome.accent, 2, 1);
        }
    }

    _checkAchievements() {
        const state = {
            maxH: this.maxH, maxCombo: this.maxCombo, coinsCollected: this.coinsCollected,
            usedShield: this.usedShield, biomesVisited: this.biomesVisited.size,
            eventsSurvived: this.eventsSurvived, encountersCompleted: this.encounters.completed,
        };
        for (const a of ACHIEVEMENT_DEFS) {
            if (this.unlockedAchievements.has(a.id)) continue;
            if (a.check(state)) {
                this.unlockedAchievements.add(a.id);
                this.achievementDisplay = a;
                this.achievementDisplayT = 200;
                this.sound.achievement();
                this._saveAchievements();
            }
        }
    }

    _checkArtifacts() {
        if (Math.random() > 0.003) return;
        const available = ARTIFACT_DEFS.filter(a =>
            this.maxH >= a.minM && this.maxH <= a.maxM &&
            !this.collectedArtifacts.has(a.id) &&
            Math.random() < a.rarity
        );
        if (available.length > 0) {
            const art = available[0];
            this.artifacts.push({ ...art, x: rand(30, this.w - 30), y: this.camY + rand(50, this.h - 80), collected: false, colAnim: 0, bob: rand(0, 6.28) });
        }
    }

    _buildEventCtx() {
        const e = this.events;
        const ctx = {};
        // Base event modifiers
        if (e.is(EVT.GOLD_RUSH) || e.is(EVT.COIN_FRENZY)) ctx.coinMult = 4;
        if (e.is(EVT.SAFE_HAVEN)) { ctx.platOverride = 'safe'; ctx.gapMod = 0.7; }
        if (e.is(EVT.BOUNCY_WORLD)) ctx.platOverride = 'bouncy';
        if (e.is(EVT.PLATFORM_RAIN)) ctx.gapMod = 0.5;
        if (e.is(EVT.GIFT_SHOWER)) ctx.puChanceMult = 8;
        if (e.is(EVT.SPIRAL_ASCENT)) ctx.spiral = true;
        if (e.is(EVT.WIDE_PLATFORMS)) ctx.widePlat = 1.8;
        if (e.is(EVT.COIN_TRAIL) || e.is(EVT.GOLDEN_AGE)) ctx.coinMult = (ctx.coinMult || 1) * 3;

        // Overlay combo event modifiers
        if (this._comboPlatOverride) ctx.platOverride = this._comboPlatOverride;
        if (this._comboGapMod !== 1) ctx.gapMod = (ctx.gapMod || 1) * this._comboGapMod;
        if (this._comboCoinMult !== 1) ctx.coinMult = (ctx.coinMult || 1) * this._comboCoinMult;
        if (this._comboSpiral) ctx.spiral = true;

        return ctx;
    }

    _updateEvents() {
        const justStarted = this.events.update(this.maxH);
        if (justStarted) {
            this.eventAnnounce = justStarted;
            this.eventAnnounceT = 140;
            this.sound.event();
            this.shake(4);
            this.particles.spawn(this.w / 2, this.p.y - 40, 15, this.currentBiome.accent, 1.5, 1);
        }
        if (!this.events.active && this._lastEventActive) this.eventsSurvived++;
        this._lastEventActive = !!this.events.active;

        const e = this.events;
        if (!this.comboEvents.active) {
            this.gravMod = 1;
            this.timeMod = 1;
        }
        if (e.is(EVT.LOW_GRAVITY) || e.is(EVT.ZERO_G)) this.gravMod = e.is(EVT.ZERO_G) ? 0.3 : 0.5;
        if (e.is(EVT.SPEED_ZONE)) this.timeMod = 1.25;

        if (e.is(EVT.COIN_FRENZY) && this.frame % 25 === 0) {
            this.coins.push(this.gen._mkCoin(rand(20, this.w - 20), this.camY + rand(50, this.h - 50)));
        }
        if (e.is(EVT.MAGNET_FIELD)) {
            for (const c of this.coins) {
                if (c.collected) continue;
                const dx = (this.p.x + this.p.w / 2) - (c.x + c.w / 2);
                const dy = (this.p.y + this.p.h / 2) - (c.y + c.h / 2);
                const d = Math.hypot(dx, dy);
                if (d < 200 && d > 1) { c.x += dx / d * 2.5; c.y += dy / d * 2.5; }
            }
        }
        this.doubleJumpAvail = e.is(EVT.DOUBLE_JUMP);
        if (this.eventAnnounceT > 0) this.eventAnnounceT--;
        if (this.eventAnnounceT <= 0) this.eventAnnounce = null;
    }

    _updateComboEvents() {
        const justStarted = this.comboEvents.update(this.maxH, this);
        if (justStarted) {
            this.comboEventAnnounce = justStarted;
            this.comboEventAnnounceT = 150;
            this.sound.comboEvent();
            this.shake(4);
            this.particles.spawn(this.w / 2, this.p.y - 40, 18, this.currentBiome.accent, 1.8, 1);
        }
        if (this.comboEventAnnounceT > 0) this.comboEventAnnounceT--;
        if (this.comboEventAnnounceT <= 0) this.comboEventAnnounce = null;
    }

    _updateEncounters() {
        if (this.encounterActive) {
            if (this.p.y < this.encounterEndY) {
                this.encounterActive = false;
                this.encounters.complete();
                this.sound.encounterComplete();
                this._float(this.w / 2, this.p.y - 50, '✓ ENCOUNTER COMPLETE!', '#22c55e', true);
                this.score += 200;
                this.shake(3);
            }
            return;
        }
        const enc = this.encounters.tryStart(this.maxH);
        if (enc) {
            this.encounterAnnounce = enc;
            this.encounterAnnounceT = 120;
            this.encounterActive = true;
            this.sound.encounterStart();
            const hi = this.plats.reduce((m, pl) => Math.min(m, pl.y), Infinity);
            const result = this.encounterGen.generate(enc.id, hi - 80, this.w);
            this.plats.push(...result.plats);
            this.coins.push(...result.coins);
            this.encounterEndY = result.endY - 50;
            const safePlat = this.gen._mkPlat(this.w / 2 - PLW / 2, result.endY - 60, PT.NORMAL);
            this.plats.push(safePlat);
            this.gen.last = [safePlat];
        }
        if (this.encounterAnnounceT > 0) this.encounterAnnounceT--;
        if (this.encounterAnnounceT <= 0) this.encounterAnnounce = null;
    }

    _updatePhantom() {
        if (!this.gen.unlockedHazards.has('phantom')) return;
        const p = this.p;
        if (!this.phantom) this.phantom = { x: this.w / 2, y: p.y + this.h, phase: 0, warn: 0 };
        const ph = this.phantom;
        ph.phase += 0.02;
        const tx = p.x + p.w / 2 + Math.sin(ph.phase) * 60;
        const ty = p.y + 200 + Math.sin(ph.phase * 0.7) * 40;
        ph.x = lerp(ph.x, tx, 0.012 + this.diff * 0.008);
        ph.y = lerp(ph.y, ty, 0.01 + this.diff * 0.006);
        const dist = Math.hypot(ph.x - (p.x + p.w / 2), ph.y - (p.y + p.h / 2));
        if (dist < 25 && !p.jet) {
            if (p.shield) {
                p.shield = false; p.shieldBreak = 1; p.vy = SJF;
                this.sound.shieldBreak(); this.shake(5);
                this.particles.spawn(p.x + p.w / 2, p.y + p.h / 2, 12, '#3b82f6', 1.5);
                this.phantom.y = p.y + 400;
                this.usedShield = true;
            } else this._die();
        }
        ph.warn = dist < 120 ? Math.min(ph.warn + 0.03, 1) : Math.max(ph.warn - 0.01, 0);
    }

    _die() {
        if (!this.reviveUsed && this.maxH > 200) {
            this.reviveUsed = true;
            this.p.vy = SJF; this.p.y -= 50;
            this.sound.revive(); this.shake(6);
            this.particles.spawn(this.p.x + this.p.w / 2, this.p.y + this.p.h / 2, 20, '#fbbf24', 2, 1);
            this._float(this.p.x + this.p.w / 2, this.p.y - 40, '💖 SECOND CHANCE!', '#f472b6', true);
            return;
        }
        this.over = true; this.overT = 0; this.sound.gameOver();
    }

    update() {
        if (this.over) { this.overT = Math.min(this.overT + 0.025, 1); return; }
        this.frame++;
        const p = this.p;
        const time = Date.now() * 0.001;

        this._checkUnlocks();
        this._checkBiome();
        this._checkMutators();
        this._checkEpochs();
        this._updateEvents();
        this._updateComboEvents();
        this._updateEncounters();
        this.diffWave.update(this.maxH);
        this._updatePhantom();
        if (this.frame % 60 === 0) this._checkAchievements();
        if (this.frame % 120 === 0) this._checkArtifacts();

        if (this.unlockDisplayT > 0) this.unlockDisplayT--;
        if (this.unlockDisplayT <= 0) this.unlockDisplay = null;
        if (this.teaserT > 0) this.teaserT--;
        if (this.teaserT <= 0) this.teaserDisplay = null;
        if (this.mutatorAnnounceT > 0) this.mutatorAnnounceT--;
        if (this.mutatorAnnounceT <= 0) this.mutatorAnnounce = null;
        if (this.achievementDisplayT > 0) this.achievementDisplayT--;
        if (this.achievementDisplayT <= 0) this.achievementDisplay = null;
        if (this.artifactDisplayT > 0) this.artifactDisplayT--;
        if (this.artifactDisplayT <= 0) this.artifactDisplay = null;
        if (this.epochAnnounceT > 0) this.epochAnnounceT--;
        if (this.epochAnnounceT <= 0) this.epochAnnounce = null;
        if (this.nearMissFlash > 0) this.nearMissFlash -= 0.05;

        // Player scale
        if (p.tiny && p.tinyT > 0) { this.targetPlayerScale = 0.55; p.tinyT--; if (p.tinyT <= 0) { p.tiny = false; } }
        else if (p.giant && p.giantT > 0) { this.targetPlayerScale = 1.6; p.giantT--; if (p.giantT <= 0) { p.giant = false; } }
        else this.targetPlayerScale = 1;
        this.playerScale = lerp(this.playerScale, this.targetPlayerScale, 0.1);

        this.zoomLevel = lerp(this.zoomLevel, this.targetZoom, 0.05);
        this.targetZoom = 1;

        // Input
        let inp = 0;
        if (this.keys.left) inp -= 1;
        if (this.keys.right) inp += 1;
        if (this.touchX !== null) inp = clamp((this.touchX - this.w / 2) / (this.w * 0.22), -1, 1);

        if (this.hasMutator('magneticField')) {
            p.vx += (this.w / 2 - (p.x + p.w / 2)) * 0.002;
        }

        const friction = p.onIce ? 0.97 : MF;
        const accel = p.onIce ? MA * 0.4 : MA;
        if (inp !== 0) { p.vx += inp * accel; p.vx = clamp(p.vx, -MS, MS); p.faceR = inp > 0; }
        else { p.vx *= friction; if (Math.abs(p.vx) < 0.08) p.vx = 0; }
        p.onIce = false;

        let grav = G * this.gravMod;
        if (p.gravInverted && p.gravInvertT > 0) {
            grav *= -0.7; p.gravInvertT--;
            if (p.gravInvertT <= 0) p.gravInverted = false;
        }

        let speedMult = 1;
        if (this.hasMutator('speedDemon')) speedMult = 1 + clamp(this.maxH / 30000, 0, 0.4);

        if (p.jet && p.jetFuel > 0) {
            p.vy = -8; p.jetFuel--;
            if (this.frame % 3 === 0) this.particles.spawn(p.x + p.w / 2 + rand(-4, 4), p.y + p.h, 1, this.currentBiome.accent);
            if (p.jetFuel <= 0) p.jet = false;
        } else {
            p.vy += grav;
        }

        const spd = (p.timeSlow && p.timeSlowT > 0 ? 0.5 : this.timeMod) * speedMult;
        p.x += p.vx * spd; p.y += p.vy * spd;
        if (p.timeSlow) { p.timeSlowT--; if (p.timeSlowT <= 0) p.timeSlow = false; }

        // Wall wrap
        if (this.hasMutator('bounceWalls')) {
            if (p.x + p.w < 0) { p.x = 0; p.vx = Math.abs(p.vx) * 0.8; p.vy += JF * 0.3; this.sound.bounceWall(); }
            else if (p.x > this.w) { p.x = this.w - p.w; p.vx = -Math.abs(p.vx) * 0.8; p.vy += JF * 0.3; this.sound.bounceWall(); }
        } else {
            if (p.x + p.w < -15) p.x = this.w + 15;
            if (p.x > this.w + 15) p.x = -p.w - 15;
        }

        p.sq = lerp(p.sq, p.sqT, 0.2);
        p.sqT = lerp(p.sqT, 1, 0.14);
        p.jAnim *= 0.9;
        if (this.frame % 3 === 0) p.trail.push({ x: p.x + p.w / 2, y: p.y + p.h / 2, a: 0.4 });
        if (p.trail.length > 8) p.trail.shift();
        for (const t of p.trail) t.a *= 0.88;

        if (p.shield) { p.shieldT--; if (p.shieldT <= 0) p.shield = false; }
        if (p.magnet) { p.magnetT--; if (p.magnetT <= 0) p.magnet = false; }
        if (p.shieldBreak > 0) p.shieldBreak -= 0.03;
        if (this.comboT > 0) { this.comboT--; if (this.comboT <= 0) this.combo = 0; }

        if (p.magnet) {
            for (const c of this.coins) {
                if (c.collected) continue;
                const dx = (p.x + p.w / 2) - (c.x + c.w / 2), dy = (p.y + p.h / 2) - (c.y + c.h / 2);
                const d = Math.hypot(dx, dy);
                if (d < 130 && d > 1) { c.x += dx / d * 3.5; c.y += dy / d * 3.5; }
            }
        }

        for (const pl of this.plats) {
            if (pl.type === PT.GHOST) pl.ghostVis = Math.sin(time * 1.5 + pl.ghostPh) > -0.3;
            if (pl.type === PT.PHASE) pl.ghostVis = Math.sin(time * 1.2 + pl.phasePh) > 0;
        }

        // Platform collision
        const pw2 = p.w * this.playerScale, ph2 = p.h * this.playerScale;
        const isGoingDown = p.gravInverted ? p.vy < 0 : p.vy > 0;

        if (isGoingDown && !p.jet) {
            for (const pl of this.plats) {
                if (pl.breaking && pl.breakTimer > 0.2) continue;
                if (pl.type === PT.GHOST && !pl.ghostVis) continue;
                if (pl.type === PT.PHASE && !pl.ghostVis) continue;
                if (pl.type === PT.TIMED && pl.timedLife <= 0) continue;

                const collY = p.gravInverted ? p.y : p.y + ph2;
                const collCheck = p.gravInverted
                    ? (collY <= pl.y + pl.h && collY >= pl.y - Math.abs(p.vy) - 2)
                    : (collY >= pl.y && collY <= pl.y + pl.h + Math.abs(p.vy) + 2);

                if (p.x + pw2 > pl.x + 5 && p.x < pl.x + pl.w - 5 && collCheck) {
                    p.y = p.gravInverted ? pl.y + pl.h : pl.y - ph2;
                    this.combo++; this.comboT = 100; this.jumpCount++;
                    if (this.combo > this.maxCombo) this.maxCombo = this.combo;
                    if (this.combo > 1) this.sound.combo(Math.min(this.combo, 12));

                    const isEchoJump = this.hasMutator('echoJump') && this.jumpCount % 3 === 0;
                    const baseJF = this.events.is(EVT.BOUNCY_WORLD) ? SJF : JF;
                    const jf = p.gravInverted ? -baseJF : baseJF;
                    const sjf = p.gravInverted ? -SJF : SJF;

                    if (pl.type === PT.SPRING || pl.type === PT.TRAMPOLINE_CHAIN || isEchoJump) {
                        this.targetZoom = 0.96;
                    }

                    switch (pl.type) {
                        case PT.BREAKING:
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            this.sound.jump();
                            this.particles.spawn(p.x + p.w / 2, p.y + ph2, 4, this.currentBiome.particleColor);
                            setTimeout(() => { p.sqT = 1.1; }, 50);
                            pl.crackAnim = 1;
                            setTimeout(() => { pl.breaking = true; pl.breakPieces = this._breakPcs(pl); this.sound.breakPlat(); }, 180);
                            break;
                        case PT.CRUMBLE_CHAIN:
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            this.sound.jump();
                            if (!pl.chainTriggered) {
                                pl.chainTriggered = true;
                                const nearby = this.plats.filter(pp => pp !== pl && Math.abs(pp.y - pl.y) < 90 && Math.abs(pp.x - pl.x) < 150 && !pp.breaking);
                                let delay = 200;
                                for (const np of nearby) {
                                    setTimeout(() => { np.crackAnim = 1; }, delay);
                                    setTimeout(() => { np.breaking = true; np.breakPieces = this._breakPcs(np); this.sound.breakPlat(); }, delay + 180);
                                    delay += 120;
                                }
                                setTimeout(() => { pl.crackAnim = 1; }, 100);
                                setTimeout(() => { pl.breaking = true; pl.breakPieces = this._breakPcs(pl); this.sound.breakPlat(); }, 300);
                            }
                            break;
                        case PT.SPRING: case PT.TRAMPOLINE_CHAIN:
                            p.vy = sjf; pl.springAnim = 1;
                            this.sound.superJump(); this.shake(4);
                            p.sqT = 1.5; p.jAnim = 1;
                            this.particles.spawn(p.x + p.w / 2, p.y + ph2, 8, '#fbbf24', 1.4);
                            { const pts = Math.floor(15 * (1 + this.combo * 0.1)); this.score += pts; this._float(p.x + p.w / 2, p.y - 8, `+${pts}`, '#fbbf24'); }
                            break;
                        case PT.TELEPORT: {
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            this.sound.teleport(); this.shake(2);
                            const pair = this.plats.find(pp => pp !== pl && pp.type === PT.TELEPORT && pp.teleportPairId === pl.teleportPairId);
                            if (pair) {
                                pl.teleportFlash = 1; pair.teleportFlash = 1;
                                this.particles.spawn(p.x + p.w / 2, p.y + ph2 / 2, 8, '#c084fc', 1.5);
                                setTimeout(() => { p.x = pair.x + pair.w / 2 - p.w / 2; p.y = pair.y - ph2; p.vy = sjf * 0.8; }, 100);
                            }
                            break;
                        }
                        case PT.CONVEYOR:
                            p.vy = isEchoJump ? sjf : jf * 1.2;
                            p.vx += pl.convDir * pl.convSpd * 0.5;
                            p.sqT = 0.7; p.jAnim = 1;
                            this.sound.convey();
                            break;
                        case PT.ICE:
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            p.onIce = true; this.sound.jump();
                            break;
                        case PT.MAGNET_PLAT:
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            this.sound.magnetPull();
                            this.particles.spawn(p.x + p.w / 2, p.y + ph2, 5, '#ec4899', 1.3);
                            for (const c of this.coins) {
                                if (c.collected) continue;
                                const d = Math.hypot((pl.x + pl.w / 2) - (c.x + c.w / 2), pl.y - c.y);
                                if (d < pl.magnetR) { c.x = lerp(c.x, p.x + p.w / 2 - c.w / 2, 0.3); c.y = lerp(c.y, p.y + p.h / 2 - c.h / 2, 0.3); }
                            }
                            break;
                        case PT.SPLIT:
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            this.sound.split();
                            if (!pl.splitDone) {
                                pl.splitDone = true;
                                const hw = pl.w * 0.4;
                                const p1 = this.gen._mkPlat(pl.x - hw * 0.5, pl.y - 60, PT.NORMAL);
                                const p2 = this.gen._mkPlat(pl.x + pl.w - hw * 0.5, pl.y - 60, PT.NORMAL);
                                p1.w = hw; p2.w = hw;
                                this.plats.push(p1, p2);
                                this.particles.spawn(pl.x + pl.w / 2, pl.y, 6, this.currentBiome.accent, 1.2, 1);
                            }
                            break;
                        case PT.GRAVITY_PLAT:
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            this.sound.gravPlat(); this.shake(2);
                            this.gravMod = 0.4;
                            setTimeout(() => { this.gravMod = 1; }, 2000);
                            this._float(p.x + p.w / 2, p.y - 20, '🌀 LOW GRAV!', '#5eead4');
                            break;
                        case PT.PHASE:
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            this.sound.jump();
                            break;
                        case PT.TIMED:
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            this.sound.jump();
                            pl.timedLife = Math.max(0, pl.timedLife - 80);
                            break;
                        default:
                            p.vy = isEchoJump ? sjf : jf; p.sqT = 0.6; p.jAnim = 1;
                            this.sound.jump();
                            this.particles.spawn(p.x + p.w / 2, p.y + ph2, 4, this.currentBiome.particleColor);
                    }

                    if (isEchoJump && pl.type !== PT.SPRING && pl.type !== PT.TRAMPOLINE_CHAIN) {
                        this._float(p.x + p.w / 2, p.y - 25, '🔊 ECHO!', '#a78bfa');
                        this.particles.spawn(p.x + p.w / 2, p.y + ph2, 6, '#a78bfa', 1.5);
                    }

                    setTimeout(() => { p.sqT = 1.1; }, 50);
                    pl.hit = true;
                    this.doubleJumpUsed = false;

                    const scoreMult = this.events.is(EVT.GOLDEN_AGE) ? 5 : 1;
                    if (this.combo >= 3 && this.combo % 3 === 0) {
                        const bonus = this.combo * 10 * scoreMult;
                        this.score += bonus;
                        this._float(p.x + p.w / 2, p.y - 28, `COMBO x${this.combo}! +${bonus}`, '#fbbf24', true);
                    }
                    break;
                }
            }
        }

        // Magnet platform pull
        for (const pl of this.plats) {
            if (pl.type === PT.MAGNET_PLAT && !pl.breaking) {
                const dx = (pl.x + pl.w / 2) - (p.x + pw2 / 2);
                const dy = pl.y - (p.y + ph2 / 2);
                const dist = Math.hypot(dx, dy);
                if (dist < pl.magnetR && dist > 5 && p.vy > 0) {
                    const force = pl.magnetStr * (1 - dist / pl.magnetR);
                    p.vx += (dx / dist) * force;
                    p.vy += (dy / dist) * force * 0.3;
                }
            }
        }

        // Coin collection
        const coinValMult = (this.events.is(EVT.COIN_FRENZY) ? 3 : 1) * (this.events.is(EVT.GOLDEN_AGE) ? 5 : 1) * this._comboCoinMult;
        for (const c of this.coins) {
            if (c.collected) continue;
            if (Math.hypot((p.x + p.w / 2) - (c.x + c.w / 2), (p.y + p.h / 2) - (c.y + c.h / 2)) < 20 * this.playerScale) {
                c.collected = true; c.colAnim = 1;
                this.coinsCollected++;
                const pts = Math.floor(25 * (1 + this.combo * 0.1) * coinValMult);
                this.score += pts; this.sound.collect();
                this.particles.spawn(c.x + c.w / 2, c.y + c.h / 2, 4, '#fbbf24');
                this._float(c.x + c.w / 2, c.y - 5, `+${pts}`, '#fbbf24');
            }
        }

        // Powerup collection
        for (const pw of this.pus) {
            if (pw.collected) continue;
            if (Math.hypot((p.x + p.w / 2) - (pw.x + pw.w / 2), (p.y + p.h / 2) - (pw.y + pw.h / 2)) < 26 * this.playerScale) {
                pw.collected = true; pw.colAnim = 1;
                this.sound.powerup(); this.shake(3);
                this.particles.spawn(pw.x + pw.w / 2, pw.y + pw.h / 2, 6, this.currentBiome.accent, 1.2);
                if (pw.type === PUT.JETPACK) { p.jet = true; p.jetFuel = 130; this.sound.jetpack(); this._float(pw.x + pw.w / 2, pw.y - 12, '🚀 JETPACK!', '#f97316', true); }
                else if (pw.type === PUT.SHIELD) { p.shield = true; p.shieldT = 650; this.sound.shield(); this._float(pw.x + pw.w / 2, pw.y - 12, '🛡️ SHIELD!', '#3b82f6', true); }
                else if (pw.type === PUT.MAGNET) { p.magnet = true; p.magnetT = 500; this._float(pw.x + pw.w / 2, pw.y - 12, '🧲 MAGNET!', '#ec4899', true); }
                else if (pw.type === PUT.TIMESLOW) { p.timeSlow = true; p.timeSlowT = 400; this.sound.timeSlow(); this._float(pw.x + pw.w / 2, pw.y - 12, '⏳ SLOW TIME!', '#a78bfa', true); }
                else if (pw.type === PUT.TINY) { p.tiny = true; p.tinyT = 350; p.giant = false; this.sound.tiny(); this._float(pw.x + pw.w / 2, pw.y - 12, '🔬 TINY!', '#34d399', true); }
                else if (pw.type === PUT.GIANT) { p.giant = true; p.giantT = 300; p.tiny = false; this.sound.giant(); this._float(pw.x + pw.w / 2, pw.y - 12, '🦖 GIANT!', '#f97316', true); }
            }
        }

        // Artifact collection
        for (const art of this.artifacts) {
            if (art.collected) continue;
            if (Math.hypot((p.x + p.w / 2) - art.x, (p.y + p.h / 2) - art.y) < 30) {
                art.collected = true; art.colAnim = 1;
                this.collectedArtifacts.add(art.id);
                this._saveArtifacts();
                this.sound.artifact(); this.shake(5);
                this.particles.spawn(art.x, art.y, 12, '#fbbf24', 2, 1);
                this.artifactDisplay = art; this.artifactDisplayT = 200;
                this.score += 500;
                this._float(art.x, art.y - 20, `${art.icon} ${art.name}!`, '#fbbf24', true);
            }
        }

        // Hazard updates
        for (const hz of this.hazards) {
            if (!hz.active) continue;
            if (hz.kind === 'rocket') {
                if (hz.warn > 0) { hz.warn--; continue; }
                if (!hz.warnDone) { hz.warnDone = true; hz.x = hz.fromLeft ? -36 : this.w + 4; this.sound.rocketWarn(); }
                hz.x += hz.vx * this.timeMod;
                if (hz.x < -60 || hz.x > this.w + 60) { hz.active = false; continue; }
                const rDist = Math.hypot((p.x + pw2 / 2) - (hz.x + hz.w / 2), (p.y + ph2 / 2) - (hz.y + hz.h / 2));
                if (rDist < 35 && rDist > 20 && !p.jet && this.nearMissFlash <= 0) {
                    this.nearMissCount++; this.nearMissFlash = 1;
                    this.sound.nearMiss(); this.score += 50;
                    this._float(p.x + p.w / 2, p.y - 30, 'NEAR MISS! +50', '#f97316');
                }
                if (!p.jet && p.x + pw2 > hz.x + 4 && p.x < hz.x + hz.w - 4 && p.y + ph2 > hz.y + 2 && p.y < hz.y + hz.h - 2) {
                    if (p.shield) {
                        p.shield = false; p.shieldBreak = 1; this.sound.shieldBreak(); this.shake(4);
                        this.particles.spawn(p.x + p.w / 2, p.y + p.h / 2, 10, '#3b82f6', 1.5);
                        hz.active = false; this.usedShield = true;
                    } else this._die();
                }
            } else if (hz.kind === 'blackhole') {
                hz.life--;
                if (hz.life <= 0) { hz.active = false; continue; }
                const dx = hz.x - (p.x + p.w / 2), dy = hz.y - (p.y + p.h / 2), dist = Math.hypot(dx, dy);
                if (dist < 120 && dist > 1 && !p.jet) {
                    const force = clamp(0.25 * (1 - dist / 120), 0, 0.6);
                    p.vx += (dx / dist) * force; p.vy += (dy / dist) * force * 0.4;
                }
                if (dist < 15 && !p.jet) {
                    if (p.shield) {
                        p.shield = false; p.shieldBreak = 1; p.vy = SJF;
                        this.sound.shieldBreak(); this.shake(5); hz.active = false; this.usedShield = true;
                    } else this._die();
                }
            } else if (hz.kind === 'homingMine') {
                hz.life--;
                if (hz.life <= 0) { hz.active = false; continue; }
                const dx = (p.x + pw2 / 2) - hz.x, dy = (p.y + ph2 / 2) - hz.y;
                const dist = Math.hypot(dx, dy);
                if (dist > 1) { hz.vx += (dx / dist) * hz.trackStr; hz.vy += (dy / dist) * hz.trackStr; hz.vx *= 0.98; hz.vy *= 0.98; }
                hz.x += hz.vx; hz.y += hz.vy;
                if (dist < 15 && !p.jet) {
                    if (p.shield) {
                        p.shield = false; p.shieldBreak = 1; p.vy = JF;
                        this.sound.shieldBreak(); this.shake(4); hz.active = false; this.usedShield = true;
                    } else this._die();
                }
            }
        }
        this.hazards = this.hazards.filter(h => h.active);

        // Meteors
        for (const m of this.meteors) {
            if (!m.active) continue;
            m.y += m.vy; m.x += m.vx;
            m.trail.push({ x: m.x, y: m.y, a: 1 });
            if (m.trail.length > 6) m.trail.shift();
            for (const t of m.trail) t.a *= 0.8;
            if (m.y > this.camY + this.h + 50) { m.active = false; continue; }
            const dist = Math.hypot(m.x - (p.x + p.w / 2), (m.y - this.camY) - (p.y - this.camY + ph2 / 2));
            if (dist < m.r + 12 && !p.jet) {
                if (p.shield) { p.shield = false; p.shieldBreak = 1; this.sound.shieldBreak(); this.shake(3); m.active = false; this.usedShield = true; }
                else this._die();
            }
        }
        this.meteors = this.meteors.filter(m => m.active);
        if (this.gen.unlockedHazards.has('meteor') && this.frame % 140 === 0 && Math.random() < 0.25) {
            this.meteors.push(this.gen._mkMeteor(this.w));
        }

        // Camera
        const cT = p.y - this.h * 0.38;
        if (cT < this.camY) this.targetCamY = cT;
        this.camY = lerp(this.camY, this.targetCamY, 0.1);

        // Score — using HEIGHT_SCALE for realistic meters
        const hs = Math.floor(Math.max(0, (this.h - 100 - p.y) / HEIGHT_SCALE));
        if (hs > this.maxH) { this.maxH = hs; this.score = Math.max(this.score, hs); }
        this.diff = clamp(this.maxH / 2000, 0, 1);
        this.gen.diff = this.diffWave.intensity;
        this.gen.height = this.maxH;

        // Milestone
        const cm = Math.floor(this.maxH / 100) * 100;
        if (cm > this.lastMile && cm > 0) {
            this.lastMile = cm; this.sound.milestone();
            this._float(this.w / 2, p.y - 55, `🏔️ ${cm}m`, this.currentBiome.accent, true);
        }

        // Generate new platforms
        const topE = this.camY - 180;
        const hi = this.plats.reduce((m, pl) => Math.min(m, pl.y), Infinity);
        if (hi > topE) {
            this.gen.last = this.plats.filter(pl => !pl.breaking && pl.type !== PT.BREAKING).sort((a, b) => a.y - b.y).slice(0, 6);
            const evtCtx = this._buildEventCtx();
            const wideMod = this.events.is(EVT.WIDE_PLATFORMS) ? 1.8 :
                this._comboWideMod !== 1 ? this._comboWideMod :
                    (this.hasMutator('platDecay') ? Math.max(0.6, 1 - this.maxH / 50000) : 1);
            const { plats, coins, pus, hazards } = this.gen.generate(hi, topE - 350, evtCtx, wideMod);
            this.plats.push(...plats); this.coins.push(...coins); this.pus.push(...pus); this.hazards.push(...hazards);
        }

        // Cleanup
        const bot = this.camY + this.h + 180;
        this.plats = this.plats.filter(pl => pl.y < bot);
        this.coins = this.coins.filter(c => c.y < bot && !(c.collected && c.colAnim <= 0));
        this.pus = this.pus.filter(pw => pw.y < bot && !(pw.collected && pw.colAnim <= 0));
        this.artifacts = this.artifacts.filter(a => a.y < bot && !a.collected);

        // Update platforms
        for (const pl of this.plats) {
            if (pl.type === PT.MOVING && !pl.breaking) {
                pl.x = pl.moveOX + Math.sin(time * pl.moveSpd) * pl.moveRange;
                pl.x = clamp(pl.x, 5, this.w - pl.w - 5);
            }
            if (pl.type === PT.ORBITING && !pl.breaking) {
                pl.x = pl.orbitCX + Math.cos(time * pl.orbitSpd + pl.orbitPh) * pl.orbitR - pl.w / 2;
                pl.y = pl.orbitCY + Math.sin(time * pl.orbitSpd + pl.orbitPh) * pl.orbitR * 0.5;
            }
            if (pl.type === PT.TIMED && !pl.breaking) {
                pl.timedLife--;
                if (pl.timedLife <= 0) { pl.breaking = true; pl.breakPieces = this._breakPcs(pl); this.sound.breakPlat(); }
            }
            if (pl.breaking) pl.breakTimer += 0.025;
            if (pl.springAnim > 0) pl.springAnim *= 0.87;
            if (pl.appearAnim > 0) pl.appearAnim *= 0.9;
            if (pl.crackAnim > 0 && !pl.breaking) pl.crackAnim = Math.min(pl.crackAnim + 0.08, 1);
            if (pl.teleportFlash > 0) pl.teleportFlash *= 0.92;
            if (pl.eventHighlight > 0) pl.eventHighlight *= 0.95;
        }

        this.particles.update();
        for (const ft of this.floats) { ft.y += ft.vy; ft.vy *= 0.98; ft.life -= 0.012; }
        this.floats = this.floats.filter(f => f.life > 0);

        if (this.shakeD > 0) {
            this.shakeX = rand(-this.shakeD, this.shakeD);
            this.shakeY = rand(-this.shakeD, this.shakeD);
            this.shakeD *= 0.87;
            if (this.shakeD < 0.08) { this.shakeD = 0; this.shakeX = 0; this.shakeY = 0; }
        }

        // Fall death
        if (p.y > this.camY + this.h + 65) {
            if (p.shield) {
                p.shield = false; p.shieldBreak = 1; p.vy = SJF;
                this.sound.shieldBreak(); this.shake(5);
                this.particles.spawn(p.x + p.w / 2, p.y + p.h / 2, 10, '#3b82f6', 1.5);
                this.usedShield = true;
            } else this._die();
        }
    }

    _breakPcs(pl) {
        const pcs = [];
        for (let i = 0; i < 5; i++) {
            const pw = pl.w / 5;
            pcs.push({ x: pl.x + i * pw, y: pl.y, w: pw - 1, h: pl.h, vx: rand(-2.5, 2.5), vy: rand(-3, 0.5), rot: 0, rv: rand(-0.12, 0.12) });
        }
        return pcs;
    }

    /* ══════════════════════════════════════════
       DRAWING
       ══════════════════════════════════════════ */
    draw() {
        const c = this.ctx, w = this.w, h = this.h;
        const B = this.currentBiome;
        const time = Date.now() * 0.001;

        c.save();

        const bg = c.createLinearGradient(0, 0, 0, h);
        bg.addColorStop(0, B.bg2); bg.addColorStop(1, B.bg1);
        c.fillStyle = bg; c.fillRect(0, 0, w, h);

        if (this.biomeTransition > 0.5) {
            c.globalAlpha = (this.biomeTransition - 0.5) * 0.12;
            c.fillStyle = B.accent; c.fillRect(0, 0, w, h); c.globalAlpha = 1;
        }

        if (this.events.is(EVT.RAINBOW_ROAD)) {
            const grad = c.createLinearGradient(0, 0, w, 0);
            const hueOff = (time * 60) % 360;
            for (let i = 0; i <= 6; i++) grad.addColorStop(i / 6, `hsla(${(hueOff + i * 60) % 360},70%,60%,0.04)`);
            c.fillStyle = grad; c.fillRect(0, 0, w, h);
        }

        if (this.nearMissFlash > 0) {
            c.globalAlpha = this.nearMissFlash * 0.08;
            c.fillStyle = '#f97316'; c.fillRect(0, 0, w, h); c.globalAlpha = 1;
        }

        this.bg.draw(c, this.camY, B, time);

        c.save();
        if (this.zoomLevel !== 1) {
            c.translate(w / 2, h / 2); c.scale(this.zoomLevel, this.zoomLevel); c.translate(-w / 2, -h / 2);
        }
        c.translate(this.shakeX, -this.camY + this.shakeY);

        this._drawHazards(c, w, h, time);

        for (const m of this.meteors) {
            if (!m.active) continue;
            for (const t of m.trail) {
                c.globalAlpha = t.a * 0.3; c.fillStyle = '#f97316';
                c.beginPath(); c.arc(t.x, t.y, m.r * 0.6, 0, 6.283); c.fill();
            }
            c.globalAlpha = 0.9; c.fillStyle = '#fbbf24'; c.shadowColor = '#f97316'; c.shadowBlur = 10;
            c.beginPath(); c.arc(m.x, m.y, m.r, 0, 6.283); c.fill();
            c.shadowBlur = 0; c.globalAlpha = 1;
        }

        if (this.phantom && this.gen.unlockedHazards.has('phantom')) {
            const ph = this.phantom;
            const pulse = 0.3 + 0.2 * Math.sin(time * 4);
            c.globalAlpha = pulse + ph.warn * 0.3;
            const grad = c.createRadialGradient(ph.x, ph.y, 0, ph.x, ph.y, 25);
            grad.addColorStop(0, '#ef444466'); grad.addColorStop(0.5, '#ef444422'); grad.addColorStop(1, 'transparent');
            c.fillStyle = grad; c.beginPath(); c.arc(ph.x, ph.y, 25, 0, 6.283); c.fill();
            c.fillStyle = '#ef4444'; c.beginPath(); c.arc(ph.x, ph.y, 5 + ph.warn * 3, 0, 6.283); c.fill();
            c.fillStyle = '#000'; c.beginPath(); c.arc(ph.x, ph.y, 2 + ph.warn, 0, 6.283); c.fill();
            c.globalAlpha = 1;
        }

        this._drawPlatforms(c, w, h, time, B);
        this._drawCoins(c, h, time);
        this._drawPowerups(c, h, time, B);
        this._drawArtifacts(c, h, time);
        this.particles.draw(c);
        this._drawPlayer(c, time, B);

        for (const ft of this.floats) {
            c.globalAlpha = ft.life;
            c.fillStyle = ft.color;
            c.font = ft.big ? 'bold 15px sans-serif' : 'bold 12px monospace';
            c.textAlign = 'center';
            c.shadowColor = ft.color; c.shadowBlur = ft.big ? 10 : 5;
            c.fillText(ft.text, ft.x, ft.y);
            c.shadowBlur = 0;
        }
        c.globalAlpha = 1;

        c.restore();
        this._hud(time);
        this._drawAnnouncements(c, w, h, time, B);
        if (this.over) this._drawOver();
        c.restore();
    }

    _drawHazards(c, w, h, time) {
        for (const hz of this.hazards) {
            if (!hz.active) continue;
            if (hz.kind === 'blackhole') {
                const pulse = 0.5 + 0.5 * Math.sin(time * 2 + hz.phase);
                c.save(); c.translate(hz.x, hz.y);
                for (let i = 3; i >= 0; i--) {
                    c.globalAlpha = 0.08 + i * 0.02;
                    c.strokeStyle = i % 2 === 0 ? '#a855f7' : '#6366f1'; c.lineWidth = 1;
                    c.beginPath(); c.arc(0, 0, hz.r + i * 8 + pulse * 4, 0, 6.283); c.stroke();
                }
                c.globalAlpha = 0.8;
                const coreG = c.createRadialGradient(0, 0, 0, 0, 0, hz.r);
                coreG.addColorStop(0, '#000'); coreG.addColorStop(0.6, '#1a0030'); coreG.addColorStop(1, 'rgba(100,0,200,0.3)');
                c.fillStyle = coreG; c.beginPath(); c.arc(0, 0, hz.r, 0, 6.283); c.fill();
                c.globalAlpha = 1; c.restore();
            } else if (hz.kind === 'rocket') {
                if (hz.warn > 0) {
                    const blink = Math.sin(time * 15) > 0;
                    if (blink) {
                        c.save(); c.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
                        const sy = hz.y - this.camY;
                        c.fillStyle = '#ef4444'; c.globalAlpha = 0.8;
                        c.font = 'bold 14px sans-serif';
                        c.textAlign = hz.fromLeft ? 'left' : 'right';
                        c.fillText('⚠', hz.fromLeft ? 8 : w - 8, sy + 6);
                        c.globalAlpha = 1; c.restore();
                        c.save(); c.translate(this.shakeX, -this.camY + this.shakeY);
                        if (this.zoomLevel !== 1) { c.translate(w / 2, this.h / 2); c.scale(this.zoomLevel, this.zoomLevel); c.translate(-w / 2, -this.h / 2); }
                    }
                } else {
                    c.save(); c.translate(hz.x + hz.w / 2, hz.y + hz.h / 2);
                    if (hz.vx < 0) c.scale(-1, 1);
                    c.fillStyle = '#ef4444'; c.shadowColor = 'rgba(239,68,68,0.5)'; c.shadowBlur = 8;
                    c.beginPath(); c.moveTo(hz.w / 2 + 4, 0); c.lineTo(hz.w / 2 - 2, -hz.h / 2); c.lineTo(hz.w / 2 - 2, hz.h / 2); c.fill();
                    c.fillStyle = '#dc2626'; c.fillRect(-hz.w / 2, -hz.h / 2, hz.w, hz.h); c.shadowBlur = 0;
                    const flameW = 6 + Math.random() * 6;
                    c.fillStyle = '#fbbf24'; c.globalAlpha = 0.7;
                    c.beginPath(); c.moveTo(-hz.w / 2, -3); c.lineTo(-hz.w / 2 - flameW, 0); c.lineTo(-hz.w / 2, 3); c.fill();
                    c.globalAlpha = 1; c.restore();
                }
            } else if (hz.kind === 'homingMine') {
                const pulse = 0.5 + 0.5 * Math.sin(time * 6);
                c.globalAlpha = 0.8; c.fillStyle = '#ef4444';
                c.shadowColor = '#ef4444'; c.shadowBlur = 8 + pulse * 4;
                c.beginPath(); c.arc(hz.x, hz.y, hz.r, 0, 6.283); c.fill();
                c.shadowBlur = 0;
                c.fillStyle = '#fbbf24'; c.globalAlpha = pulse;
                c.beginPath(); c.arc(hz.x, hz.y, 2, 0, 6.283); c.fill();
                c.globalAlpha = 1;
            }
        }
    }

    _drawPlatforms(c, w, h, time, B) {
        for (const pl of this.plats) {
            const screenY = pl.y - this.camY;
            if (screenY < -50 || screenY > h + 50) continue;

            if (pl.breaking && pl.breakTimer > 0.2 && pl.breakPieces) {
                for (const pc of pl.breakPieces) {
                    pc.x += pc.vx; pc.y += pc.vy; pc.vy += 0.15; pc.rot += pc.rv;
                    c.save();
                    c.translate(pc.x + pc.w / 2, pc.y + pc.h / 2); c.rotate(pc.rot);
                    c.globalAlpha = clamp(1 - pl.breakTimer * 0.7, 0, 1);
                    c.fillStyle = B.platColor;
                    this._rr(c, -pc.w / 2, -pc.h / 2, pc.w, pc.h, 3);
                    c.globalAlpha = 1; c.restore();
                }
                continue;
            }

            if (pl.crackAnim > 0 && !pl.breaking) {
                c.save(); c.translate(rand(-pl.crackAnim * 3, pl.crackAnim * 3), rand(-pl.crackAnim * 2, pl.crackAnim * 2));
            }

            if (pl.type === PT.GHOST) c.globalAlpha = clamp(Math.sin(time * 1.5 + pl.ghostPh) * 0.65 + 0.4, 0.08, 0.82);
            if (pl.type === PT.PHASE) c.globalAlpha = clamp(Math.sin(time * 1.2 + pl.phasePh) * 0.8 + 0.2, 0.05, 0.9);
            if (pl.type === PT.TIMED && pl.timedLife < 100) c.globalAlpha = clamp(pl.timedLife / 100, 0.2, 1);

            const scale = pl.appearAnim > 0.01 ? easeOutBack(1 - pl.appearAnim) : 1;
            c.save();
            c.translate(pl.x + pl.w / 2, pl.y + pl.h / 2); c.scale(scale, scale);

            let col = B.platColor, glow = B.platGlow;
            switch (pl.type) {
                case PT.SPRING: case PT.TRAMPOLINE_CHAIN: col = '#f59e0b'; glow = 'rgba(245,158,11,0.4)'; break;
                case PT.MOVING: col = '#06b6d4'; glow = 'rgba(6,182,212,0.35)'; break;
                case PT.BREAKING: case PT.CRUMBLE_CHAIN: col = '#6b7280'; glow = ''; break;
                case PT.GHOST: col = '#8b5cf6'; glow = 'rgba(139,92,246,0.3)'; break;
                case PT.TELEPORT: col = '#c084fc'; glow = 'rgba(192,132,252,0.4)'; break;
                case PT.CONVEYOR: col = '#14b8a6'; glow = 'rgba(20,184,166,0.35)'; break;
                case PT.ICE: col = '#7dd3fc'; glow = 'rgba(125,211,252,0.35)'; break;
                case PT.ORBITING: col = '#a78bfa'; glow = 'rgba(167,139,250,0.35)'; break;
                case PT.YOURSIZE: col = '#fbbf24'; glow = 'rgba(251,191,36,0.35)'; break;
                case PT.MAGNET_PLAT: col = '#ec4899'; glow = 'rgba(236,72,153,0.35)'; break;
                case PT.PHASE: col = '#c084fc'; glow = 'rgba(192,132,252,0.3)'; break;
                case PT.SPLIT: col = '#34d399'; glow = 'rgba(52,211,153,0.35)'; break;
                case PT.GRAVITY_PLAT: col = '#2dd4bf'; glow = 'rgba(45,212,191,0.4)'; break;
                case PT.TIMED: col = '#f472b6'; glow = 'rgba(244,114,182,0.35)'; break;
            }

            if (glow) { c.shadowColor = glow; c.shadowBlur = 12; }
            c.fillStyle = col;
            this._rr(c, -pl.w / 2, -pl.h / 2, pl.w, pl.h, 8);
            c.shadowBlur = 0;
            c.fillStyle = 'rgba(255,255,255,0.14)';
            this._rr(c, -pl.w / 2 + 2, -pl.h / 2, pl.w - 4, pl.h / 3, 4);

            if (pl.type === PT.BREAKING || pl.type === PT.CRUMBLE_CHAIN) {
                c.strokeStyle = '#4b5563'; c.lineWidth = 1; c.globalAlpha = 0.5;
                c.beginPath(); c.moveTo(-pl.w * 0.3, -1); c.lineTo(0, 2); c.lineTo(pl.w * 0.2, -1); c.stroke();
                c.globalAlpha = 1;
            }
            if (pl.type === PT.SPRING || pl.type === PT.TRAMPOLINE_CHAIN) {
                const sS = 1 + pl.springAnim * 0.7, sH = 11 * sS;
                c.fillStyle = '#fcd34d';
                this._rr(c, -6, -pl.h / 2 - sH, 12, sH, 4);
                c.strokeStyle = '#d97706'; c.lineWidth = 1.5;
                for (let i = 0; i < 3; i++) { const yy = -pl.h / 2 - sH + 3 + i * (sH / 3); c.beginPath(); c.moveTo(-4, yy); c.lineTo(4, yy); c.stroke(); }
            }
            if (pl.type === PT.MOVING) { c.fillStyle = 'rgba(255,255,255,0.25)'; c.font = '8px sans-serif'; c.textAlign = 'center'; c.fillText('◄►', 0, 3); }
            if (pl.type === PT.TELEPORT) {
                const tp = 0.5 + 0.5 * Math.sin(time * 4);
                c.strokeStyle = '#c084fc'; c.lineWidth = 1; c.globalAlpha = 0.3 + tp * 0.3;
                c.beginPath(); c.arc(0, 0, 10 + tp * 4, 0, 6.283); c.stroke();
                c.globalAlpha = 1;
                if (pl.teleportFlash > 0.01) {
                    c.globalAlpha = pl.teleportFlash * 0.5; c.fillStyle = '#c084fc';
                    c.beginPath(); c.arc(0, 0, 20 * pl.teleportFlash, 0, 6.283); c.fill(); c.globalAlpha = 1;
                }
            }
            if (pl.type === PT.CONVEYOR) { c.fillStyle = 'rgba(255,255,255,0.4)'; c.font = '7px sans-serif'; c.textAlign = 'center'; c.fillText(pl.convDir > 0 ? '→→→' : '←←←', 0, 3); }
            if (pl.type === PT.ICE) { c.fillStyle = 'rgba(255,255,255,0.3)'; c.font = '7px sans-serif'; c.textAlign = 'center'; c.fillText('❄', 0, 3); }
            if (pl.type === PT.MAGNET_PLAT) {
                c.globalAlpha = 0.06 + 0.03 * Math.sin(time * 3);
                c.strokeStyle = '#ec4899'; c.lineWidth = 1;
                c.beginPath(); c.arc(0, 0, pl.magnetR * 0.5, 0, 6.283); c.stroke(); c.globalAlpha = 1;
            }
            if (pl.type === PT.SPLIT) { c.fillStyle = 'rgba(255,255,255,0.4)'; c.font = '8px sans-serif'; c.textAlign = 'center'; c.fillText(pl.splitDone ? '✓' : '✂', 0, 3); }
            if (pl.type === PT.GRAVITY_PLAT) {
                c.fillStyle = 'rgba(255,255,255,0.3)'; c.font = '8px sans-serif'; c.textAlign = 'center'; c.fillText('🌀', 0, 3);
            }
            if (pl.type === PT.PHASE) { c.fillStyle = 'rgba(255,255,255,0.3)'; c.font = '8px sans-serif'; c.textAlign = 'center'; c.fillText('🔮', 0, 3); }
            if (pl.type === PT.TIMED) {
                const pct = pl.timedLife / pl.timedMax;
                c.fillStyle = pct > 0.3 ? '#22c55e' : '#ef4444';
                c.globalAlpha = 0.6;
                const barW = pl.w * 0.8;
                this._rr(c, -barW / 2, pl.h / 2 + 2, barW * pct, 3, 2);
                c.globalAlpha = 1;
            }
            if (pl.type === PT.YOURSIZE) { c.fillStyle = 'rgba(255,255,255,0.3)'; c.font = '7px sans-serif'; c.textAlign = 'center'; c.fillText('📐', 0, 3); }
            if (pl.type === PT.ORBITING) {
                c.strokeStyle = 'rgba(167,139,250,0.2)'; c.lineWidth = 1;
                c.beginPath(); c.ellipse(pl.orbitCX - (pl.x + pl.w / 2), pl.orbitCY - (pl.y + pl.h / 2), pl.orbitR, pl.orbitR * 0.5, 0, 0, 6.283); c.stroke();
            }

            if (pl.eventHighlight > 0.01) {
                c.globalAlpha = pl.eventHighlight * 0.4;
                c.strokeStyle = B.accent; c.lineWidth = 2;
                c.beginPath(); this._rrP(c, -pl.w / 2 - 3, -pl.h / 2 - 3, pl.w + 6, pl.h + 6, 10); c.stroke();
                c.globalAlpha = 1;
            }

            c.restore();
            if (pl.type === PT.GHOST || pl.type === PT.PHASE) c.globalAlpha = 1;
            if (pl.type === PT.TIMED && pl.timedLife < 100) c.globalAlpha = 1;
            if (pl.crackAnim > 0 && !pl.breaking) c.restore();
        }
    }

    _drawCoins(c, h, time) {
        for (const co of this.coins) {
            const sy = co.y - this.camY;
            if (sy < -20 || sy > h + 20) continue;
            if (co.collected) {
                if (co.colAnim > 0) {
                    co.colAnim -= 0.06;
                    c.globalAlpha = co.colAnim;
                    const sc = 1 + (1 - co.colAnim) * 0.5;
                    c.save(); c.translate(co.x + co.w / 2, co.y + co.h / 2); c.scale(sc, sc);
                    c.fillStyle = '#fbbf24'; c.beginPath(); c.arc(0, 0, co.w / 2, 0, 6.283); c.fill();
                    c.restore(); c.globalAlpha = 1;
                }
                continue;
            }
            const bob = Math.sin(time * 3 + co.bob) * 3;
            c.save(); c.translate(co.x + co.w / 2, co.y + co.h / 2 + bob);
            c.shadowColor = 'rgba(251,191,36,0.4)'; c.shadowBlur = 7;
            c.fillStyle = '#fbbf24'; c.beginPath(); c.arc(0, 0, co.w / 2, 0, 6.283); c.fill();
            c.shadowBlur = 0;
            c.fillStyle = 'rgba(255,255,255,0.4)'; c.beginPath(); c.arc(-1.5, -1.5, co.w / 5, 0, 6.283); c.fill();
            c.restore();
        }
    }

    _drawPowerups(c, h, time, B) {
        for (const pw of this.pus) {
            const sy = pw.y - this.camY;
            if (sy < -25 || sy > h + 25) continue;
            if (pw.collected) {
                if (pw.colAnim > 0) {
                    pw.colAnim -= 0.04;
                    c.globalAlpha = pw.colAnim;
                    const sc = 1 + (1 - pw.colAnim);
                    c.save(); c.translate(pw.x + pw.w / 2, pw.y + pw.h / 2); c.scale(sc, sc);
                    c.fillStyle = B.accent; c.beginPath(); c.arc(0, 0, pw.w / 2, 0, 6.283); c.fill();
                    c.restore(); c.globalAlpha = 1;
                }
                continue;
            }
            const bob = Math.sin(time * 2.5 + pw.bob) * 4;
            const pulse = 1 + Math.sin(time * 4 + pw.bob) * 0.07;
            c.save(); c.translate(pw.x + pw.w / 2, pw.y + pw.h / 2 + bob); c.scale(pulse, pulse);
            const pcol = pw.type === PUT.JETPACK ? '#f97316' : pw.type === PUT.SHIELD ? '#3b82f6' : pw.type === PUT.MAGNET ? '#ec4899' : pw.type === PUT.TIMESLOW ? '#a78bfa' : pw.type === PUT.TINY ? '#34d399' : '#f97316';
            c.shadowColor = pcol; c.shadowBlur = 10;
            c.fillStyle = pcol + '33'; c.beginPath(); c.arc(0, 0, pw.w / 2 + 4, 0, 6.283); c.fill();
            c.fillStyle = pcol; c.beginPath(); c.arc(0, 0, pw.w / 2, 0, 6.283); c.fill();
            c.shadowBlur = 0;
            c.fillStyle = '#fff'; c.font = 'bold 10px sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
            const puIcon = pw.type === PUT.JETPACK ? '🚀' : pw.type === PUT.SHIELD ? '🛡' : pw.type === PUT.MAGNET ? '🧲' : pw.type === PUT.TIMESLOW ? '⏳' : pw.type === PUT.TINY ? '🔬' : '🦖';
            c.fillText(puIcon, 0, 0);
            c.restore();
        }
    }

    _drawArtifacts(c, h, time) {
        for (const art of this.artifacts) {
            if (art.collected) continue;
            const sy = art.y - this.camY;
            if (sy < -30 || sy > h + 30) continue;
            const bob = Math.sin(time * 2 + art.bob) * 5;
            const pulse = 1 + Math.sin(time * 3 + art.bob) * 0.12;
            const glow = 0.3 + 0.2 * Math.sin(time * 4 + art.bob);
            c.save(); c.translate(art.x, art.y + bob); c.scale(pulse, pulse);
            c.globalAlpha = glow; c.fillStyle = '#fbbf24';
            c.shadowColor = '#fbbf24'; c.shadowBlur = 20;
            c.beginPath(); c.arc(0, 0, 16, 0, 6.283); c.fill(); c.shadowBlur = 0;
            c.globalAlpha = 0.9; c.fillStyle = '#1a1a2e';
            c.beginPath(); c.arc(0, 0, 12, 0, 6.283); c.fill();
            c.globalAlpha = 1; c.fillStyle = '#fbbf24';
            c.font = 'bold 14px sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
            c.fillText(art.icon, 0, 1);
            for (let i = 0; i < 4; i++) {
                const angle = time * 2 + i * Math.PI / 2;
                const sx = Math.cos(angle) * 18, sy2 = Math.sin(angle) * 18;
                c.globalAlpha = 0.4 + 0.3 * Math.sin(time * 5 + i);
                c.fillStyle = '#fde047';
                c.beginPath(); c.arc(sx, sy2, 1.5, 0, 6.283); c.fill();
            }
            c.globalAlpha = 1; c.restore();
        }
    }

    _drawPlayer(c, time, B) {
        const p = this.p;
        const pw2 = p.w * this.playerScale, ph2 = p.h * this.playerScale;
        for (const tr of p.trail) {
            if (tr.a < 0.02) continue;
            c.globalAlpha = tr.a * 0.2; c.fillStyle = B.accent;
            c.beginPath(); c.arc(tr.x, tr.y, 3, 0, 6.283); c.fill();
        }
        c.globalAlpha = 1;

        c.save();
        c.translate(p.x + p.w / 2, p.y + p.h / 2);
        const pScale = this.playerScale;
        c.scale((p.faceR ? 1 : -1) * pScale / p.sq, p.sq * pScale);

        if (p.shield) {
            c.globalAlpha = 0.18 + 0.12 * Math.sin(time * 4);
            c.strokeStyle = '#3b82f6'; c.lineWidth = 2;
            c.shadowColor = '#3b82f6'; c.shadowBlur = 8;
            c.beginPath(); c.arc(0, 0, p.w * 0.78, 0, 6.283); c.stroke();
            c.shadowBlur = 0; c.globalAlpha = 1;
        }
        if (p.shieldBreak > 0) {
            c.globalAlpha = p.shieldBreak; c.fillStyle = '#3b82f6';
            c.beginPath(); c.arc(0, 0, p.w * (1.5 - p.shieldBreak * 0.5), 0, 6.283); c.fill();
            c.globalAlpha = 1;
        }

        c.shadowColor = B.accentGlow; c.shadowBlur = 12 + p.jAnim * 10;
        const bG = c.createLinearGradient(0, -p.h / 2, 0, p.h / 2);
        bG.addColorStop(0, B.accent + 'cc'); bG.addColorStop(1, B.accent);
        c.fillStyle = bG;
        this._rr(c, -p.w / 2, -p.h / 2, p.w, p.h, 10);
        c.shadowBlur = 0;

        const ey = -4, es = 5;
        c.fillStyle = '#fff';
        c.beginPath(); c.ellipse(-es, ey, 4.5, 5.5, 0, 0, 6.283); c.fill();
        c.beginPath(); c.ellipse(es, ey, 4.5, 5.5, 0, 0, 6.283); c.fill();
        const px = clamp(p.vx * 0.3, -2, 2), py = clamp(p.vy * 0.1, -1.5, 1.5);
        c.fillStyle = '#1a1a2e';
        c.beginPath(); c.arc(-es + px, ey + py, 2.5, 0, 6.283); c.fill();
        c.beginPath(); c.arc(es + px, ey + py, 2.5, 0, 6.283); c.fill();
        c.fillStyle = 'rgba(255,255,255,0.65)';
        c.beginPath(); c.arc(-es + px - 0.8, ey + py - 0.8, 0.8, 0, 6.283); c.fill();
        c.beginPath(); c.arc(es + px - 0.8, ey + py - 0.8, 0.8, 0, 6.283); c.fill();

        c.strokeStyle = '#1a1a2e'; c.lineWidth = 1.5; c.lineCap = 'round'; c.beginPath();
        if (p.jet) { c.arc(0, 3, 4, 0, Math.PI); }
        else if (p.vy < -3) { c.arc(0, 4, 3.5, 0.15 * Math.PI, 0.85 * Math.PI); }
        else if (p.vy > 6) { c.arc(0, 5, 2, 0, 6.283); }
        else { c.moveTo(-3, 5); c.lineTo(3, 5); }
        c.stroke();

        if (p.jet && p.jetFuel > 0) {
            c.fillStyle = '#f97316';
            this._rr(c, -p.w / 2 - 4, 2, 5, 10, 2);
            this._rr(c, p.w / 2 - 1, 2, 5, 10, 2);
            const fH = 5 + Math.random() * 7;
            const fG = c.createLinearGradient(0, p.h / 2, 0, p.h / 2 + fH);
            fG.addColorStop(0, '#fbbf24'); fG.addColorStop(0.5, '#f97316'); fG.addColorStop(1, 'rgba(239,68,68,0)');
            c.fillStyle = fG;
            c.beginPath(); c.moveTo(-5, p.h / 2); c.lineTo(0, p.h / 2 + fH); c.lineTo(5, p.h / 2); c.fill();
        }

        if (p.magnet) {
            c.globalAlpha = 0.12 + 0.08 * Math.sin(time * 5);
            c.strokeStyle = '#ec4899'; c.lineWidth = 1;
            for (let i = 1; i <= 2; i++) {
                c.beginPath(); c.arc(0, 0, p.w * 0.5 + i * 12 + Math.sin(time * 3 + i) * 3, 0, 6.283); c.stroke();
            }
            c.globalAlpha = 1;
        }

        if (p.timeSlow && p.timeSlowT > 0) {
            c.globalAlpha = 0.15 + 0.1 * Math.sin(time * 2);
            c.strokeStyle = '#a78bfa'; c.lineWidth = 1.5;
            c.beginPath(); c.arc(0, 0, p.w * 0.9, 0, 6.283); c.stroke();
            c.globalAlpha = 1;
        }

        if (p.gravInverted && p.gravInvertT > 0) {
            c.globalAlpha = 0.2 + 0.1 * Math.sin(time * 6);
            c.strokeStyle = '#5eead4'; c.lineWidth = 1;
            c.beginPath(); c.arc(0, 0, p.w * 0.7, 0, 6.283); c.stroke();
            c.fillStyle = '#5eead4'; c.globalAlpha = 0.4;
            c.beginPath(); c.moveTo(0, -p.h / 2 - 8); c.lineTo(-4, -p.h / 2 - 3); c.lineTo(4, -p.h / 2 - 3); c.fill();
            c.globalAlpha = 1;
        }

        c.restore();
    }

    _drawAnnouncements(c, w, h, time, B) {
        // Event announcement
        if (this.eventAnnounce && this.eventAnnounceT > 0) {
            const a = clamp(this.eventAnnounceT > 110 ? (140 - this.eventAnnounceT) / 30 : this.eventAnnounceT / 30, 0, 1);
            const yOff = (1 - easeOutBack(clamp(a * 1.2, 0, 1))) * 40;
            c.globalAlpha = a;
            c.save(); c.translate(w / 2, h * 0.3 + yOff);
            c.fillStyle = 'rgba(10,10,10,0.92)';
            c.strokeStyle = B.accent + '66'; c.lineWidth = 2;
            c.beginPath(); this._rrP(c, -120, -30, 240, 60, 18); c.fill(); c.stroke();
            c.fillStyle = B.accent;
            c.font = 'bold 14px sans-serif'; c.textAlign = 'center';
            c.shadowColor = B.accent; c.shadowBlur = 12;
            c.fillText(this.eventAnnounce.name, 0, -4); c.shadowBlur = 0;
            c.fillStyle = '#aaa'; c.font = '10px sans-serif';
            c.fillText(this.eventAnnounce.desc, 0, 16);
            c.restore(); c.globalAlpha = 1;
        }

        // Combo event announcement
        if (this.comboEventAnnounce && this.comboEventAnnounceT > 0) {
            const a = clamp(this.comboEventAnnounceT > 120 ? (150 - this.comboEventAnnounceT) / 30 : this.comboEventAnnounceT / 30, 0, 1);
            const yOff = (1 - easeOutBack(clamp(a * 1.2, 0, 1))) * 40;
            c.globalAlpha = a;
            c.save(); c.translate(w / 2, h * 0.28 + yOff);
            c.fillStyle = 'rgba(10,10,10,0.95)';
            c.strokeStyle = '#c084fc66'; c.lineWidth = 2;
            c.beginPath(); this._rrP(c, -130, -34, 260, 68, 18); c.fill(); c.stroke();
            c.fillStyle = '#c084fc';
            c.font = 'bold 14px sans-serif'; c.textAlign = 'center';
            c.shadowColor = '#c084fc'; c.shadowBlur = 12;
            c.fillText(this.comboEventAnnounce.name, 0, -8); c.shadowBlur = 0;
            c.fillStyle = '#999'; c.font = '9px sans-serif';
            c.fillText(this.comboEventAnnounce.desc, 0, 10);
            c.fillStyle = '#666'; c.font = '8px sans-serif';
            c.fillText('COMBO EVENT', 0, 24);
            c.restore(); c.globalAlpha = 1;
        }

        // Epoch announcement
        if (this.epochAnnounce && this.epochAnnounceT > 0) {
            const a = clamp(this.epochAnnounceT > 210 ? (250 - this.epochAnnounceT) / 40 : this.epochAnnounceT / 50, 0, 1);
            const yOff = (1 - easeOutBack(clamp(a * 1.2, 0, 1))) * 50;
            const scale = 0.8 + a * 0.2;
            c.globalAlpha = a;
            c.save(); c.translate(w / 2, h * 0.35 + yOff); c.scale(scale, scale);
            c.fillStyle = 'rgba(5,5,5,0.96)';
            c.strokeStyle = '#fbbf2466'; c.lineWidth = 2;
            c.beginPath(); this._rrP(c, -140, -42, 280, 84, 22); c.fill(); c.stroke();
            // Inner glow line
            c.strokeStyle = '#fbbf2422'; c.lineWidth = 1;
            c.beginPath(); this._rrP(c, -136, -38, 272, 76, 20); c.stroke();
            c.fillStyle = '#fbbf24';
            c.font = 'bold 11px sans-serif'; c.textAlign = 'center';
            c.fillText('✦ NEW EPOCH ✦', 0, -18);
            c.font = 'bold 20px sans-serif';
            c.shadowColor = '#fbbf24'; c.shadowBlur = 15;
            c.fillText(`${this.epochAnnounce.icon} ${this.epochAnnounce.name}`, 0, 8); c.shadowBlur = 0;
            c.fillStyle = '#888'; c.font = '10px sans-serif';
            c.fillText(this.epochAnnounce.desc, 0, 28);
            c.restore(); c.globalAlpha = 1;
        }

        // Encounter announcement
        if (this.encounterAnnounce && this.encounterAnnounceT > 0) {
            const a = clamp(this.encounterAnnounceT > 90 ? (120 - this.encounterAnnounceT) / 30 : this.encounterAnnounceT / 30, 0, 1);
            const yOff = (1 - easeOutBack(clamp(a * 1.2, 0, 1))) * 30;
            c.globalAlpha = a;
            c.save(); c.translate(w / 2, h * 0.22 + yOff);
            c.fillStyle = 'rgba(10,10,10,0.92)';
            c.strokeStyle = '#22c55e66'; c.lineWidth = 2;
            c.beginPath(); this._rrP(c, -110, -24, 220, 48, 16); c.fill(); c.stroke();
            c.fillStyle = '#22c55e';
            c.font = 'bold 13px sans-serif'; c.textAlign = 'center';
            c.shadowColor = '#22c55e'; c.shadowBlur = 10;
            c.fillText(this.encounterAnnounce.name, 0, -2); c.shadowBlur = 0;
            c.fillStyle = '#888'; c.font = '9px sans-serif';
            c.fillText('Complete the pattern!', 0, 14);
            c.restore(); c.globalAlpha = 1;
        }

        // Mutator announcement
        if (this.mutatorAnnounce && this.mutatorAnnounceT > 0) {
            const a = clamp(this.mutatorAnnounceT > 150 ? (180 - this.mutatorAnnounceT) / 30 : this.mutatorAnnounceT / 30, 0, 1);
            const yOff = (1 - easeOutBack(clamp(a * 1.2, 0, 1))) * 30;
            c.globalAlpha = a;
            c.save(); c.translate(w / 2, h * 0.5 + yOff);
            c.fillStyle = 'rgba(10,10,10,0.95)';
            c.strokeStyle = '#fbbf2466'; c.lineWidth = 2;
            c.beginPath(); this._rrP(c, -125, -32, 250, 64, 18); c.fill(); c.stroke();
            c.fillStyle = '#fbbf24';
            c.font = 'bold 14px sans-serif'; c.textAlign = 'center';
            c.shadowColor = '#fbbf24'; c.shadowBlur = 12;
            c.fillText('🔥 MUTATOR ACTIVE', 0, -10); c.shadowBlur = 0;
            c.fillStyle = '#fff'; c.font = 'bold 12px sans-serif';
            c.fillText(this.mutatorAnnounce.name, 0, 8);
            c.fillStyle = '#888'; c.font = '9px sans-serif';
            c.fillText(this.mutatorAnnounce.desc, 0, 24);
            c.restore(); c.globalAlpha = 1;
        }

        // Achievement notification
        if (this.achievementDisplay && this.achievementDisplayT > 0) {
            const a = clamp(this.achievementDisplayT > 170 ? (200 - this.achievementDisplayT) / 30 : this.achievementDisplayT / 40, 0, 1);
            const yOff = (1 - easeOutBack(clamp(a * 1.2, 0, 1))) * 30;
            c.globalAlpha = a;
            c.save(); c.translate(w / 2, h - 70 + yOff);
            c.fillStyle = 'rgba(10,10,10,0.92)';
            c.strokeStyle = '#fbbf2444'; c.lineWidth = 1;
            c.beginPath(); this._rrP(c, -105, -22, 210, 44, 14); c.fill(); c.stroke();
            c.fillStyle = '#fbbf24'; c.font = 'bold 10px sans-serif'; c.textAlign = 'center';
            c.fillText('🏆 ACHIEVEMENT', 0, -6);
            c.fillStyle = '#fff'; c.font = 'bold 11px sans-serif';
            c.fillText(`${this.achievementDisplay.icon} ${this.achievementDisplay.name}`, 0, 10);
            c.restore(); c.globalAlpha = 1;
        }

        // Artifact notification
        if (this.artifactDisplay && this.artifactDisplayT > 0) {
            const a = clamp(this.artifactDisplayT > 170 ? (200 - this.artifactDisplayT) / 30 : this.artifactDisplayT / 40, 0, 1);
            const yOff = (1 - easeOutBack(clamp(a * 1.2, 0, 1))) * 30;
            c.globalAlpha = a;
            c.save(); c.translate(w / 2, h * 0.4 + yOff);
            c.fillStyle = 'rgba(10,10,10,0.95)';
            c.strokeStyle = '#fbbf2466'; c.lineWidth = 2;
            c.beginPath(); this._rrP(c, -115, -30, 230, 60, 18); c.fill(); c.stroke();
            c.fillStyle = '#fbbf24'; c.font = 'bold 12px sans-serif'; c.textAlign = 'center';
            c.shadowColor = '#fbbf24'; c.shadowBlur = 15;
            c.fillText('✨ ARTIFACT FOUND!', 0, -8); c.shadowBlur = 0;
            c.fillStyle = '#fff'; c.font = 'bold 14px sans-serif';
            c.fillText(`${this.artifactDisplay.icon} ${this.artifactDisplay.name}`, 0, 14);
            c.restore(); c.globalAlpha = 1;
        }

        // Event progress bar
        if (this.events.active) {
            const prog = this.events.progress();
            c.globalAlpha = 0.6;
            c.fillStyle = 'rgba(0,0,0,0.4)';
            this._rr(c, w / 2 - 50, h - 28, 100, 8, 4);
            c.fillStyle = B.accent + '88';
            this._rr(c, w / 2 - 50, h - 28, 100 * (1 - prog), 8, 4);
            c.globalAlpha = 0.4;
            c.fillStyle = '#fff'; c.font = '7px monospace'; c.textAlign = 'center';
            c.fillText(this.events.active.name.replace(/[^\w\s]/g, '').trim(), w / 2, h - 16);
            c.globalAlpha = 1;
        }

        // Combo event progress bar
        if (this.comboEvents.active) {
            const prog = this.comboEvents.progress();
            const barY = this.events.active ? h - 42 : h - 28;
            c.globalAlpha = 0.6;
            c.fillStyle = 'rgba(0,0,0,0.4)';
            this._rr(c, w / 2 - 50, barY, 100, 8, 4);
            c.fillStyle = '#c084fc88';
            this._rr(c, w / 2 - 50, barY, 100 * (1 - prog), 8, 4);
            c.globalAlpha = 0.4;
            c.fillStyle = '#fff'; c.font = '7px monospace'; c.textAlign = 'center';
            c.fillText(this.comboEvents.active.name.replace(/[^\w\s]/g, '').trim(), w / 2, barY - 4);
            c.globalAlpha = 1;
        }

        // Unlock notification
        if (this.unlockDisplay && this.unlockDisplayT > 0) {
            const a = clamp(this.unlockDisplayT > 170 ? (200 - this.unlockDisplayT) / 30 : this.unlockDisplayT / 40, 0, 1);
            const yOff = (1 - easeOutBack(clamp(a * 1.2, 0, 1))) * 30;
            c.globalAlpha = a;
            c.save(); c.translate(w / 2, 80 + yOff);
            c.fillStyle = 'rgba(10,10,10,0.9)';
            c.strokeStyle = B.accent + '44'; c.lineWidth = 1;
            c.beginPath(); this._rrP(c, -100, -22, 200, 44, 16); c.fill(); c.stroke();
            c.fillStyle = B.accent; c.font = 'bold 10px sans-serif'; c.textAlign = 'center';
            c.fillText('✦ NEW UNLOCK ✦', 0, -6);
            c.fillStyle = '#fff'; c.font = 'bold 12px sans-serif';
            c.fillText(`${this.unlockDisplay.icon} ${this.unlockDisplay.name}`, 0, 12);
            c.restore(); c.globalAlpha = 1;
        }

        // Teaser
        if (this.teaserDisplay && this.teaserT > 0) {
            const a = clamp(this.teaserT > 150 ? (180 - this.teaserT) / 30 : this.teaserT / 30, 0, 1);
            c.globalAlpha = a * 0.7;
            c.fillStyle = '#888'; c.font = '10px monospace'; c.textAlign = 'center';
            c.fillText(`${this.teaserDisplay.icon} ${this.teaserDisplay.text}`, w / 2, h - 45);
            c.globalAlpha = 1;
        }
    }

    _hud(time) {
        const c = this.ctx, B = this.currentBiome, w = this.w;

        c.fillStyle = B.accent + '22';
        this._rr(c, 10, 10, 95, 36, 12);
        c.fillStyle = '#fff';
        c.font = 'bold 13px monospace'; c.textAlign = 'left';
        c.fillText(`↑ ${this.score}m`, 20, 33);

        const hi = this._hi();
        if (hi > 0) {
            c.fillStyle = B.accent + '22';
            this._rr(c, w - 78, 10, 68, 36, 12);
            c.fillStyle = '#fbbf24';
            c.font = 'bold 10px monospace'; c.textAlign = 'right';
            c.fillText(`♛${hi}`, w - 18, 33);
        }

        // Biome + epoch name
        c.fillStyle = B.accent; c.globalAlpha = 0.3;
        c.font = 'bold 9px monospace'; c.textAlign = 'center';
        const epochName = this.epochSystem.getName(this.maxH);
        c.fillText(`${B.name} · ${epochName}`, w / 2, 20);
        c.globalAlpha = 1;

        if (this.combo > 1) {
            c.globalAlpha = clamp(this.comboT / 30, 0, 1);
            c.fillStyle = '#fbbf24';
            c.font = `bold ${13 + Math.min(this.combo, 8)}px sans-serif`;
            c.textAlign = 'center';
            c.shadowColor = '#fbbf24'; c.shadowBlur = 8;
            c.fillText(`x${this.combo}`, w / 2, 42);
            c.shadowBlur = 0; c.globalAlpha = 1;
        }

        const p = this.p;
        let iy = 54;
        const bar = (label, col, rem, max) => {
            const pct = rem / max, bw = 70;
            c.fillStyle = 'rgba(0,0,0,0.4)'; this._rr(c, 10, iy, bw, 14, 5);
            c.fillStyle = col + '55'; this._rr(c, 10, iy, bw * pct, 14, 5);
            c.fillStyle = col; c.font = 'bold 8px sans-serif'; c.textAlign = 'left';
            c.fillText(label, 14, iy + 10);
            iy += 18;
        };
        if (p.shield) bar('🛡 Shield', '#3b82f6', p.shieldT, 650);
        if (p.jet) bar('🚀 Jet', '#f97316', p.jetFuel, 130);
        if (p.magnet) bar('🧲 Magnet', '#ec4899', p.magnetT, 500);
        if (p.timeSlow) bar('⏳ Slow', '#a78bfa', p.timeSlowT, 400);
        if (p.tiny) bar('🔬 Tiny', '#34d399', p.tinyT, 350);
        if (p.giant) bar('🦖 Giant', '#f97316', p.giantT, 300);
        if (p.gravInverted) bar('🌀 Grav', '#5eead4', p.gravInvertT, 120);

        // Active mutators
        if (this.activeMutators.length > 0) {
            const mx = w - 12;
            let my = 54;
            c.textAlign = 'right'; c.font = '8px sans-serif';
            for (const mut of this.activeMutators) {
                c.fillStyle = 'rgba(251,191,36,0.15)';
                this._rr(c, mx - 72, my, 72, 14, 5);
                c.fillStyle = '#fbbf24'; c.globalAlpha = 0.6;
                c.fillText(mut.name, mx - 4, my + 10);
                c.globalAlpha = 1;
                my += 16;
            }
        }

        c.fillStyle = '#fbbf24'; c.globalAlpha = 0.4;
        c.font = '9px monospace'; c.textAlign = 'left';
        c.fillText(`🪙 ${this.coinsCollected}`, 14, iy + 8);
        c.globalAlpha = 1;

        if (!this.reviveUsed && this.maxH > 200) {
            c.fillStyle = '#f472b6'; c.globalAlpha = 0.3;
            c.font = '9px sans-serif'; c.textAlign = 'center';
            c.fillText('💖 Second chance ready', w / 2, this.h - 6);
            c.globalAlpha = 1;
        }

        if (this.encounterActive) {
            c.fillStyle = 'rgba(34,197,94,0.15)';
            this._rr(c, w / 2 - 55, 48, 110, 16, 6);
            c.fillStyle = '#22c55e'; c.font = 'bold 9px sans-serif'; c.textAlign = 'center';
            c.fillText('🏗️ ENCOUNTER', w / 2, 59);
        }
    }

    _drawOver() {
        const c = this.ctx, w = this.w, h = this.h, B = this.currentBiome;
        const t = easeOutElastic(clamp(this.overT * 1.5, 0, 1));

        if (this.over && !this.scoreSubmitted) {
            this.scoreSubmitted = true;
            if (this.onGameOver) this.onGameOver(this.score);
        }

        c.fillStyle = `rgba(0,0,0,${0.65 * this.overT})`; c.fillRect(0, 0, w, h);

        c.save();
        c.translate(w / 2, h / 2); c.scale(t, t);

        c.fillStyle = 'rgba(12,12,12,0.96)';
        c.strokeStyle = 'rgba(255,255,255,0.08)'; c.lineWidth = 1;
        c.beginPath(); this._rrP(c, -125, -150, 250, 300, 22); c.fill(); c.stroke();

        c.fillStyle = B.accent; c.font = 'bold 22px sans-serif'; c.textAlign = 'center';
        c.fillText('GAME OVER', 0, -110);

        c.fillStyle = '#fff'; c.font = 'bold 38px monospace';
        c.fillText(`${this.score}m`, 0, -55);

        const best = this._hi(), isNew = this.score >= best && this.score > 0;
        c.fillStyle = isNew ? '#fbbf24' : '#555';
        c.font = 'bold 11px monospace';
        c.fillText(isNew ? '★ NEW BEST!' : `BEST: ${best}m`, 0, -30);

        if (this.maxCombo > 1) {
            c.fillStyle = '#fbbf24'; c.font = 'bold 10px monospace';
            c.fillText(`MAX COMBO: x${this.maxCombo}`, 0, -12);
        }

        c.fillStyle = '#aaa'; c.font = '10px monospace';
        c.fillText(`🪙 ${this.coinsCollected} coins`, 0, 6);

        const reachedBiome = BIOMES.find(b => this.maxH >= b.startM && this.maxH < b.endM) || getProceduralBiome(this.maxH) || BIOMES[BIOMES.length - 1];
        c.fillStyle = reachedBiome.accent; c.font = 'bold 9px monospace';
        c.fillText(`⬡ ${reachedBiome.name}`, 0, 24);

        const epochName = this.epochSystem.getName(this.maxH);
        c.fillStyle = '#fbbf24'; c.font = 'bold 9px monospace';
        c.fillText(`✦ ${epochName}`, 0, 40);

        const stats = [];
        if (this.eventsSurvived > 0) stats.push(`${this.eventsSurvived} event${this.eventsSurvived > 1 ? 's' : ''} survived`);
        if (this.encounters.completed > 0) stats.push(`${this.encounters.completed} encounter${this.encounters.completed > 1 ? 's' : ''} completed`);
        if (this.nearMissCount > 0) stats.push(`${this.nearMissCount} near miss${this.nearMissCount > 1 ? 'es' : ''}`);
        if (this.activeMutators.length > 0) stats.push(`${this.activeMutators.length} mutator${this.activeMutators.length > 1 ? 's' : ''} active`);

        c.fillStyle = '#666'; c.font = '9px monospace';
        let sy = 58;
        for (const st of stats.slice(0, 3)) { c.fillText(st, 0, sy); sy += 14; }

        if (this.collectedArtifacts.size > 0) {
            c.fillStyle = '#fbbf24'; c.font = '9px monospace';
            c.fillText(`✨ ${this.collectedArtifacts.size}/${ARTIFACT_DEFS.length} artifacts`, 0, sy);
            sy += 14;
        }

        const blink = 0.5 + 0.5 * Math.sin(Date.now() * 0.005);
        c.globalAlpha = 0.3 + blink * 0.7;
        c.fillStyle = '#888'; c.font = '11px sans-serif';
        c.fillText('tap or space to retry', 0, 130);
        c.globalAlpha = 1;

        c.restore();
        this._saveHi();
    }

    _hi() { try { return parseInt(localStorage.getItem('dj_hi') || '0', 10); } catch { return 0; } }
    _saveHi() { try { if (this.score > this._hi()) localStorage.setItem('dj_hi', String(this.score)); } catch { } }
    _loadAchievements() { try { return new Set(JSON.parse(localStorage.getItem('dj_ach') || '[]')); } catch { return new Set(); } }
    _saveAchievements() { try { localStorage.setItem('dj_ach', JSON.stringify([...this.unlockedAchievements])); } catch { } }
    _loadArtifacts() { try { return new Set(JSON.parse(localStorage.getItem('dj_art') || '[]')); } catch { return new Set(); } }
    _saveArtifacts() { try { localStorage.setItem('dj_art', JSON.stringify([...this.collectedArtifacts])); } catch { } }

    _rr(c, x, y, w, h, r) {
        c.beginPath(); this._rrP(c, x, y, w, h, r); c.fill();
    }
    _rrP(c, x, y, w, h, r) {
        c.moveTo(x + r, y); c.lineTo(x + w - r, y);
        c.quadraticCurveTo(x + w, y, x + w, y + r); c.lineTo(x + w, y + h - r);
        c.quadraticCurveTo(x + w, y + h, x + w - r, y + h); c.lineTo(x + r, y + h);
        c.quadraticCurveTo(x, y + h, x, y + h - r); c.lineTo(x, y + r);
        c.quadraticCurveTo(x, y, x + r, y); c.closePath();
    }
}

/* ══════════════════════════════════════════
   REACT COMPONENT
   ══════════════════════════════════════════ */
const DoodleJumpGame = memo(({ isActive, accentColor = 'purple' }) => {
    const canvasRef = useRef(null);
    const gameRef = useRef(null);
    const sfxRef = useRef(null);
    const rafRef = useRef(null);
    const saveScoreRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [started, setStarted] = useState(false);
    const [isGameOver, setIsGameOver] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);
    const [leaderboard, setLeaderboard] = useState([]);
    const [isLoadingTop, setIsLoadingTop] = useState(false);
    const { isAuthenticated, token } = useAuth();
    const { t } = useLanguage();

    const fetchLeaderboard = useCallback(async () => {
        setIsLoadingTop(true);
        try {
            const response = await axios.get(`${config.api.baseUrl}/api/public/leaderboard/doodle-jump`);
            if (response.data && response.data.success) setLeaderboard(response.data.data);
        } catch (error) {
            console.error('Failed to fetch leaderboard:', error.response?.status, error.message);
        } finally { setIsLoadingTop(false); }
    }, []);

    const saveScore = useCallback(async (score) => {
        if (!isAuthenticated || !token || score <= 0) return;
        try {
            await axios.post(
                `${config.api.baseUrl}/api/account/score/doodle-jump`,
                { score },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchLeaderboard();
        } catch (error) { console.error('[DoodleJump] Failed to save score:', error); }
    }, [isAuthenticated, token, fetchLeaderboard]);

    useEffect(() => { saveScoreRef.current = saveScore; }, [saveScore]);
    useEffect(() => { if (isExpanded) fetchLeaderboard(); }, [isExpanded, fetchLeaderboard]);

    const baseTheme = accentColor;

    useEffect(() => { if (isExpanded && !started) startGame(); }, [isExpanded, started]);
    useEffect(() => { sfxRef.current = new SFX(); }, []);

    const startGame = useCallback(() => {
        if (!canvasRef.current || !sfxRef.current) return;
        sfxRef.current.init();
        const g = new DoodleGame(canvasRef.current, baseTheme, sfxRef.current);
        g.onGameOver = (score) => {
            setIsGameOver(true);
            if (saveScoreRef.current) saveScoreRef.current(score);
        };
        gameRef.current = g;
        setPlaying(true); setStarted(true); setIsGameOver(false);

        let lastTime = performance.now();
        const interval = 1000 / 60;
        const loop = (currentTime) => {
            rafRef.current = requestAnimationFrame(loop);
            const delta = currentTime - lastTime;
            if (delta >= interval) {
                lastTime = currentTime - (delta % interval);
                g.update(); g.draw();
            }
        };
        rafRef.current = requestAnimationFrame(loop);
    }, [baseTheme]);

    const restart = useCallback(() => { if (gameRef.current) { gameRef.current.reset(); setIsGameOver(false); } }, []);

    const onClick = useCallback((e) => {
        e.stopPropagation();
        if (!started) { startGame(); return; }
        if (gameRef.current?.over) restart();
    }, [started, startGame, restart]);

    useEffect(() => {
        if (!playing) return;
        const d = (e) => {
            const g = gameRef.current; if (!g) return;
            if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') { g.keys.left = true; e.preventDefault(); }
            if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') { g.keys.right = true; e.preventDefault(); }
            if ((e.key === ' ' || e.key === 'Enter') && g.over) { restart(); e.preventDefault(); }
        };
        const u = (e) => {
            const g = gameRef.current; if (!g) return;
            if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') g.keys.left = false;
            if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') g.keys.right = false;
        };
        window.addEventListener('keydown', d); window.addEventListener('keyup', u);
        return () => { window.removeEventListener('keydown', d); window.removeEventListener('keyup', u); };
    }, [playing, restart]);

    useEffect(() => {
        if (!playing) return;
        const cv = canvasRef.current; if (!cv) return;
        const gX = (e) => { const r = cv.getBoundingClientRect(); return (e.touches ? e.touches[0].clientX : e.clientX) - r.left; };
        const s = (e) => { if (gameRef.current) gameRef.current.touchX = gX(e); };
        const m = (e) => { if (gameRef.current) gameRef.current.touchX = gX(e); e.preventDefault(); };
        const end = () => { if (gameRef.current) gameRef.current.touchX = null; };
        cv.addEventListener('touchstart', s, { passive: true });
        cv.addEventListener('touchmove', m, { passive: false });
        cv.addEventListener('touchend', end, { passive: true });
        const md = (e) => { if (gameRef.current) gameRef.current.touchX = gX(e); };
        const mm = (e) => { if (e.buttons > 0 && gameRef.current) gameRef.current.touchX = gX(e); };
        cv.addEventListener('mousedown', md); cv.addEventListener('mousemove', mm);
        cv.addEventListener('mouseup', end); cv.addEventListener('mouseleave', end);
        return () => {
            cv.removeEventListener('touchstart', s); cv.removeEventListener('touchmove', m); cv.removeEventListener('touchend', end);
            cv.removeEventListener('mousedown', md); cv.removeEventListener('mousemove', mm);
            cv.removeEventListener('mouseup', end); cv.removeEventListener('mouseleave', end);
        };
    }, [playing]);

    useEffect(() => {
        if (!playing) return;
        const h = () => { if (gameRef.current) gameRef.current.resize(); };
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, [playing]);

    useEffect(() => {
        if (!isActive && rafRef.current) {
            cancelAnimationFrame(rafRef.current); rafRef.current = null;
            setPlaying(false); setStarted(false); gameRef.current = null;
        }
    }, [isActive]);

    useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

    const accent = { purple: '#a855f7', cyan: '#06b6d4', red: '#ef4444' }[accentColor] || '#a855f7';
    const glow = accent + '66';

    return (
        <AnimatePresence>
            {isActive && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 10 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    className="relative bg-[#0e0e0e] rounded-2xl overflow-hidden w-full h-full"
                >

                    <div
                        className="relative w-full h-full cursor-pointer select-none overflow-hidden"
                        onClick={onClick}
                    >
                        <div className="relative w-full h-full" style={{ minHeight: '420px' }}>
                            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
                            {!started && (
                                <div className="absolute inset-0 flex items-center justify-center z-10" style={{ background: 'rgba(7,6,12,0.88)' }}>
                                    <div className="text-center">
                                        <motion.div
                                            animate={{ scale: [1, 1.06, 1], rotate: [0, 2, -2, 0] }}
                                            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                                            className="rounded-2xl mx-auto mb-5 flex items-center justify-center"
                                            style={{ width: 72, height: 72, background: `linear-gradient(135deg, ${accent}, ${accent}aa)`, boxShadow: `0 0 40px ${glow}` }}
                                        >
                                            <i className="fas fa-play text-white text-2xl ml-1"></i>
                                        </motion.div>
                                        <p className="text-gray-300 text-base font-bold mb-1">Doodle Jump</p>
                                        <p className="text-gray-600 text-xs mb-1">{t('jumpAsHighAsYouCan') || 'Jump as high as you can!'}</p>
                                        <motion.p
                                            animate={{ opacity: [0.4, 1, 0.4] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                            className="text-gray-500 text-xs"
                                        >
                                            {t('tapToStart') || 'tap to start'}
                                        </motion.p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {isExpanded && (
                            <div className={`absolute top-14 right-4 w-32 z-20 pointer-events-none flex flex-col transition-opacity duration-500 ${(started && !isGameOver) ? 'opacity-20' : 'opacity-100'}`}>
                                <div className="space-y-1.5">
                                    {isLoadingTop ? (
                                        <div className="text-[9px] text-gray-600 animate-pulse">{t('loading')}</div>
                                    ) : leaderboard.length > 0 ? (
                                        leaderboard.slice(0, 5).map((entry, idx) => (
                                            <div key={idx} className="flex items-center justify-between gap-2 drop-shadow-md">
                                                <div className="flex items-center gap-1.5 overflow-hidden">
                                                    <span className={`text-[8px] font-mono shrink-0 ${idx < 3 ? 'text-yellow-500' : 'text-gray-500'}`}>{idx + 1}</span>
                                                    <span className="text-[9px] text-gray-300 truncate">{entry.name}</span>
                                                </div>
                                                <span className="text-[9px] font-mono text-gray-400 shrink-0">{entry.score}</span>
                                            </div>
                                        ))
                                    ) : null}
                                </div>
                                {isAuthenticated && (
                                    <div className="mt-2 pt-1 border-t border-white/10 drop-shadow-md">
                                        <div className="text-[8px] text-gray-500 font-mono italic">
                                            YOU: {localStorage.getItem('dj_hi') || 0}m
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
});

DoodleJumpGame.displayName = 'DoodleJumpGame';

export default DoodleJumpGame;